import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import Popover from "@mui/material/Popover";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "utils/api";
import { useTeamLocale } from "./TeamLocaleContext";
import DealMapStageHelp from "./DealMapStageHelp";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// CRA + Leaflet default icon paths break without this.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/** Dr. Hoffmann studio — Cremon 11, 20457 Hamburg */
const HQ = {
  id: "hq-cremon-11",
  lat: 53.5454515,
  lng: 9.9885532,
  companyName: "Dr. Hoffmann Massage",
  addressLine: "Cremon 11, 20457 Hamburg",
  address: {
    street: "Cremon 11",
    postalCode: "20457",
    city: "Hamburg",
    country: "Deutschland",
    fullAddress: "Cremon 11, 20457 Hamburg",
  },
  phone: "",
  dealStageLabel: "Standort",
};

/** Company visits in the generated route (final stop is always back to start). */
const MAX_ROUTE_STOPS = 24;
const UNKNOWN_STAGE_LABEL = "__unknown__";
const LOST_STAGE_LABEL = "Verloren";
const RESPONSE_WAIT_STAGE_LABEL = "Antwort abwarten";
const EXPLAINED_STAGES = ["Kupfer", "Messing", "Bronze", "Silber", "Gold"];

const STAGE_COLORS = {
  Kupfer: "#b87333",
  Messing: "#a88e1d",
  Gold: "#c9a227",
  Silber: "#7a8699",
  Silver: "#7a8699",
  Bronze: "#a65c2e",
  [RESPONSE_WAIT_STAGE_LABEL]: "#7c3aed",
  [LOST_STAGE_LABEL]: "#dc2626",
  Lost: "#dc2626",
};

const LOST_STAGE_COLOR = "#dc2626";
const UNKNOWN_STAGE_COLOR = "#111827";
const LOST_STAGE_HINTS = /verloren|lost|closed[\s_-]*lost|^k\.?\s*o\.?$/i;
const LOST_STAGE_IDS = new Set(["1377516656", "1436291090"]);
const FILTER_STAGE_LABELS = [
  "Kupfer",
  "Bronze",
  "Silber",
  "Gold",
  RESPONSE_WAIT_STAGE_LABEL,
  UNKNOWN_STAGE_LABEL,
  LOST_STAGE_LABEL,
];
const KNOWN_STAGE_LABELS = new Set([
  "Kupfer",
  "Bronze",
  "Silber",
  "Silver",
  "Gold",
  RESPONSE_WAIT_STAGE_LABEL,
]);
const ROUTE_STAGE_LABELS = new Set(["Bronze", "Silber", "Gold", UNKNOWN_STAGE_LABEL]);
const CRM_STAGE_OPTIONS = [
  "kupfer",
  "bronze",
  "silber",
  "gold",
  "antwort-abwarten",
  "unknown",
  "lost",
];

const colorForStage = (label) => {
  if (LOST_STAGE_HINTS.test(String(label || ""))) return LOST_STAGE_COLOR;
  if (!label || label === UNKNOWN_STAGE_LABEL) return UNKNOWN_STAGE_COLOR;
  if (STAGE_COLORS[label]) return STAGE_COLORS[label];
  return UNKNOWN_STAGE_COLOR;
};

const isLostPin = (pin) =>
  LOST_STAGE_IDS.has(String(pin?.dealStageId || pin?.dealstage || pin?.stageId || "")) ||
  LOST_STAGE_HINTS.test(
    String(pin?.dealStageLabel || pin?.dealStage || pin?.stageLabel || pin?.stage || "")
  );

const stageKeyForPin = (pin) => {
  if (isLostPin(pin)) return LOST_STAGE_LABEL;
  if (pin?.dealStageLabel === "Silver") return "Silber";
  if (
    String(pin?.dealStageLabel || "").trim().toLowerCase() ===
    RESPONSE_WAIT_STAGE_LABEL.toLowerCase()
  ) {
    return RESPONSE_WAIT_STAGE_LABEL;
  }
  if (KNOWN_STAGE_LABELS.has(pin?.dealStageLabel)) return pin.dealStageLabel;
  return UNKNOWN_STAGE_LABEL;
};

const displayStageForPin = (pin) => {
  if (isLostPin(pin)) return LOST_STAGE_LABEL;
  return pin?.dealStageLabel || "";
};

const colorForPin = (pin) => colorForStage(stageKeyForPin(pin));

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

const latestActivityMs = (pin) => {
  const times = [];
  const push = (value) => {
    const time = value ? new Date(value).getTime() : NaN;
    if (Number.isFinite(time)) times.push(time);
  };
  push(pin?.lastContactedAt);
  (pin?.adminNoteHistory || []).forEach((note) => {
    push(note?.updatedAt);
    push(note?.createdAt);
  });
  return times.length ? Math.max(...times) : null;
};

const wasModifiedWithinTwoWeeks = (pin) => {
  const time = latestActivityMs(pin);
  if (time == null) return false;
  return Date.now() - time < TWO_WEEKS_MS;
};

const isRouteEligiblePin = (pin) => {
  const stage = stageKeyForPin(pin);
  return (
    stage !== RESPONSE_WAIT_STAGE_LABEL &&
    ROUTE_STAGE_LABELS.has(stage) &&
    !wasModifiedWithinTwoWeeks(pin)
  );
};

const stageValueForPin = (pin) => {
  const key = stageKeyForPin(pin);
  if (key === "Kupfer") return "kupfer";
  if (key === "Bronze") return "bronze";
  if (key === "Silber" || key === "Silver") return "silber";
  if (key === "Gold") return "gold";
  if (key === RESPONSE_WAIT_STAGE_LABEL) return "antwort-abwarten";
  if (key === LOST_STAGE_LABEL) return "lost";
  return "unknown";
};

const pinDisplayName = (pin) =>
  pin?.companyName ||
  pin?.contactName ||
  pin?.contactFullName ||
  pin?.fullName ||
  pin?.dealName ||
  "";

const dealOwnerLabel = (deal) =>
  String(deal?.ownerName || deal?.owner || deal?.ownerEmail || "").trim();

const pinDealOwners = (pin) => {
  const deals = Array.isArray(pin?.deals) ? pin.deals : [];
  const seen = new Map();

  deals.forEach((deal) => {
    const name = dealOwnerLabel(deal);
    const email = String(deal?.ownerEmail || "").trim();
    const id = String(deal?.ownerId || "").trim();
    if (!name && !email && !id) return;
    const key = id || email.toLowerCase() || name.toLowerCase();
    const existing = seen.get(key);
    if (existing) {
      existing.dealCount += 1;
      return;
    }
    seen.set(key, { name, email, id, dealCount: 1 });
  });

  if (seen.size) return [...seen.values()];

  const fallbackName = String(
    pin?.dealOwnerName || pin?.activeDeal?.ownerName || pin?.activeDeal?.owner || ""
  ).trim();
  const fallbackEmail = String(
    pin?.dealOwnerEmail || pin?.activeDeal?.ownerEmail || ""
  ).trim();
  const fallbackId = String(pin?.dealOwnerId || pin?.activeDeal?.ownerId || "").trim();
  if (!fallbackName && !fallbackEmail && !fallbackId) return [];
  return [
    {
      name: fallbackName,
      email: fallbackEmail,
      id: fallbackId,
      dealCount: 1,
    },
  ];
};

const formatPinDealOwners = (pin, t) => {
  const owners = pinDealOwners(pin);
  if (!owners.length) return t("dealMap.noDealOwner");
  const names = owners.map((owner) => owner.name || owner.email).join(", ");
  const dealCount = Array.isArray(pin?.deals) ? pin.deals.filter(Boolean).length : 0;
  if (dealCount > 1) {
    return t("dealMap.dealOwnersWithCount", { names, count: dealCount });
  }
  return names;
};

const getFullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || null;

const pinNameSortValue = (pin) => pinDisplayName(pin).toLowerCase();

const lastContactMs = (pin) => latestActivityMs(pin);

const sortPins = (items, sortMode) => {
  const next = [...items];
  if (sortMode === "last-contacted-asc" || sortMode === "last-contacted-desc") {
    const direction = sortMode === "last-contacted-asc" ? 1 : -1;
    return next.sort((a, b) => {
      const ta = lastContactMs(a);
      const tb = lastContactMs(b);
      if (ta === null && tb === null) {
        return pinNameSortValue(a).localeCompare(pinNameSortValue(b), "de");
      }
      if (ta === null) return 1;
      if (tb === null) return -1;
      return (ta - tb) * direction || pinNameSortValue(a).localeCompare(pinNameSortValue(b), "de");
    });
  }
  return next.sort((a, b) => pinNameSortValue(a).localeCompare(pinNameSortValue(b), "de"));
};

const requestFullscreen = (element) => {
  if (element.requestFullscreen) {
    return element
      .requestFullscreen({ navigationUI: "hide" })
      .catch(() => element.requestFullscreen());
  }
  if (element.webkitRequestFullscreen) return element.webkitRequestFullscreen();
  return Promise.reject(new Error("Fullscreen unavailable"));
};

const exitFullscreen = () => {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  return Promise.resolve();
};

const invalidateMapIfReady = (map) => {
  try {
    const container = map?.getContainer?.();
    if (!container?.isConnected || !map?._mapPane) return;
    map.invalidateSize();
  } catch {
    // Leaflet can throw during React dev remounts while panes are being rebuilt.
  }
};

const applyMapFullscreenViewport = () => {
  const root = document.documentElement;
  const viewport = window.visualViewport;
  root.style.setProperty(
    "--team-deal-map-fullscreen-height",
    `${Math.round(viewport?.height || window.innerHeight)}px`
  );
  root.style.setProperty(
    "--team-deal-map-fullscreen-width",
    `${Math.round(viewport?.width || window.innerWidth)}px`
  );
  root.style.setProperty(
    "--team-deal-map-fullscreen-top",
    `${Math.round(viewport?.offsetTop || 0)}px`
  );
  root.style.setProperty(
    "--team-deal-map-fullscreen-left",
    `${Math.round(viewport?.offsetLeft || 0)}px`
  );
};

const clearMapFullscreenViewport = () => {
  const root = document.documentElement;
  root.style.removeProperty("--team-deal-map-fullscreen-height");
  root.style.removeProperty("--team-deal-map-fullscreen-width");
  root.style.removeProperty("--team-deal-map-fullscreen-top");
  root.style.removeProperty("--team-deal-map-fullscreen-left");
};

const makeStageIcon = (color, stopNumber = null, routeIndex = 0) => {
  if (stopNumber == null) {
    return L.divIcon({
      className: "team-deal-map-marker",
      html: `<span style="--pin-color:${color}"></span>`,
      iconSize: [22, 22],
      iconAnchor: [11, 11],
      popupAnchor: [0, -10],
    });
  }
  const routeClass =
    routeIndex > 0 ? ` team-deal-map-marker--route-${routeIndex + 1}` : "";
  return L.divIcon({
    className: `team-deal-map-marker team-deal-map-marker--stop${routeClass}`,
    html: `<span style="--pin-color:${color}">${escapeHtml(String(stopNumber))}</span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
};

const makeHqIcon = (stopNumber = null) =>
  L.divIcon({
    className: "team-deal-map-marker team-deal-map-marker--hq",
    html: stopNumber
      ? `<span class="team-deal-map-hq-wrap"><span class="team-deal-map-hq-pin" title="Cremon 11"></span><span class="team-deal-map-origin-num">${escapeHtml(
          String(stopNumber)
        )}</span></span>`
      : `<span class="team-deal-map-hq-pin" title="Cremon 11"></span>`,
    iconSize: stopNumber ? [32, 40] : [28, 36],
    iconAnchor: stopNumber ? [16, 36] : [14, 34],
    popupAnchor: [0, -30],
  });

const makeUserIcon = (stopNumber = null) =>
  L.divIcon({
    className: "team-deal-map-marker team-deal-map-marker--user",
    html: stopNumber
      ? `<span class="team-deal-map-user-pin team-deal-map-user-pin--num" aria-hidden="true">${escapeHtml(
          String(stopNumber)
        )}</span>`
      : `<span class="team-deal-map-user-pin" aria-hidden="true"><i></i></span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatSyncTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeWebsiteUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withProtocol);
    if (!/^https?:$/i.test(url.protocol)) return null;
    return url.toString();
  } catch {
    return null;
  }
};

const websiteLabel = (value) =>
  String(value || "")
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");

const pinWebsiteValue = (pin) => {
  const candidates = [pin?.website, pin?.domain].filter(Boolean);
  return candidates.find((value) => normalizeWebsiteUrl(value)) || "";
};

const EMPTY_COMPANY_DETAILS_FORM = {
  companyName: "",
  contactName: "",
  email: "",
  phone: "",
  website: "",
  domain: "",
  addressLine: "",
  street: "",
  postalCode: "",
  city: "",
  country: "",
};

const companyDetailsFormFromPin = (pin = {}) => ({
  companyName: pin.companyName || "",
  contactName: pin.contactName || "",
  email: pin.email || "",
  phone: pin.phone || "",
  website: pin.website || "",
  domain: pin.domain || "",
  addressLine: pinAddressLine(pin),
  street: pin.address?.street || "",
  postalCode: pin.address?.postalCode || "",
  city: pin.address?.city || "",
  country: pin.address?.country || "",
});

/** Approximate distance in meters (good enough for Hamburg-scale routing). */
const distanceMeters = (a, b) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

const pinAddressLine = (pin) =>
  String(pin.addressLine || pin.address?.fullAddress || "").trim();

/** True when lat/lng can be passed to Leaflet. Number(null) === 0, so never wrap null in Number(). */
const hasMapCoords = (pin) => {
  const lat = pin?.lat;
  const lng = pin?.lng;
  if (lat == null || lng == null || lat === "" || lng === "") return false;
  return Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
};

const CITY_OR_COUNTRY_ONLY =
  /^(hamburg|deutschland|germany|de|berlin|münchen|muenchen|köln|koeln)(?:\s*,\s*(deutschland|germany|de))?$/i;

const VAGUE_MAP_LOCATION =
  /^(deutschland|germany|de|bundesrepublik deutschland|federal republic of germany|italy|italien|italia|france|frankreich|austria|österreich|oesterreich|switzerland|schweiz|netherlands|niederlande|holland|spain|spanien|poland|polen|sweden|schweden|norway|norwegen|denmark|dänemark|belgium|belgien|united kingdom|great britain|england|uk|united states|usa|europe|europa|hamburg|berlin|münchen|muenchen|munich|köln|koeln|cologne)(?:\s*,\s*(deutschland|germany|de|europe|europa))?$/i;

/** Google/Nominatim centroid for "Deutschland" / "Germany". */
const GERMANY_CENTROID = { lat: 51.165691, lng: 10.451526 };

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const isCityCountryOrZip = (value) => {
  const compact = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!compact) return true;
  if (/^\d{4,5}$/.test(compact)) return true;
  return CITY_OR_COUNTRY_ONLY.test(compact);
};

/** Street-level address only — city, ZIP, or country alone is not enough for a map pin. */
const hasUsableStreet = (pin) => {
  const street = String(pin?.address?.street || "").trim();
  if (!street) return false;

  const compact = street.replace(/\s+/g, " ");
  if (isCityCountryOrZip(compact)) return false;

  const placeParts = [
    pin?.address?.city,
    pin?.address?.postalCode,
    pin?.address?.country,
    "Deutschland",
    "Germany",
    "DE",
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);

  const streetNorm = compact.toLowerCase();
  if (placeParts.some((part) => part.toLowerCase() === streetNorm)) return false;

  let leftover = compact;
  placeParts.forEach((part) => {
    leftover = leftover.replace(new RegExp(`\\b${escapeRegExp(part)}\\b`, "ig"), " ");
  });
  leftover = leftover.replace(/[,;/|]+/g, " ").replace(/\s+/g, " ").trim();

  if (!leftover || isCityCountryOrZip(leftover)) return false;
  if (/^\d{4,5}$/.test(leftover)) return false;
  if (!/[A-Za-zÄÖÜäöüß]/.test(leftover)) return false;
  return leftover.length >= 2;
};

/** True when the plotted location is only a country or city (e.g. "Deutschland"). */
const isVagueMapLocation = (pin) => {
  const line = String(pin?.geocodedAddress || pin?.addressLine || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!line) return false;
  if (isCityCountryOrZip(line)) return true;
  return VAGUE_MAP_LOCATION.test(line);
};

const isCountryCentroid = (pin) => {
  const lat = Number(pin?.lat);
  const lng = Number(pin?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return (
    Math.abs(lat - GERMANY_CENTROID.lat) < 0.001 &&
    Math.abs(lng - GERMANY_CENTROID.lng) < 0.001
  );
};

const HAMBURG_PLZ = new Set(
  `20095 20259 20537 20535 20459 20457 20359 20357 20355 20354
   20257 20097 20255 20253 20251 20249 20149 20148 20146 20144
   20099 20539 21075 21129 21109 21107 21079 21077 21039 21073
   21037 21035 21033 21031 21029 21147 22419 22459 22457 22455
   22453 22391 22417 22415 22397 22395 22393 22525 22523 22587
   22527 22529 22547 22549 22559 22339 22589 22605 22607 22609
   22761 22763 22765 22767 22769 22359 22399 22337 22119 22041
   22043 22045 22335 22049 22081 22083 22085 22087 22089 22111
   22113 22115 22117 22047 22143 22299 22309 22307 22305 22303
   22145 22301 22297 22179 22177 22175 22159 22149 22147`.trim().split(/\s+/)
);

const HAMBURG_BOUNDS = { minLat: 53.38, maxLat: 53.75, minLng: 9.7, maxLng: 10.35 };

const cityLooksLikeHamburg = (value) => /\bhamburg\b/i.test(String(value || "").trim());

const extractPostalCodes = (value) => String(value || "").match(/\b\d{5}\b/g) || [];

const isInHamburg = (pin) => {
  const city = String(pin?.address?.city || "").trim();
  if (city) return cityLooksLikeHamburg(city);

  const postalCodes = [
    ...extractPostalCodes(pin?.address?.postalCode),
    ...extractPostalCodes(pin?.address?.fullAddress),
    ...extractPostalCodes(pin?.addressLine),
    ...extractPostalCodes(pin?.geocodedAddress),
  ];
  if (postalCodes.some((code) => HAMBURG_PLZ.has(code))) return true;
  if (postalCodes.length) return false;

  const line = [pin?.address?.fullAddress, pin?.addressLine, pin?.geocodedAddress]
    .filter(Boolean)
    .join(" ");
  if (cityLooksLikeHamburg(line)) return true;

  if (hasMapCoords(pin)) {
    const lat = Number(pin.lat);
    const lng = Number(pin.lng);
    if (
      lat >= HAMBURG_BOUNDS.minLat &&
      lat <= HAMBURG_BOUNDS.maxLat &&
      lng >= HAMBURG_BOUNDS.minLng &&
      lng <= HAMBURG_BOUNDS.maxLng
    ) {
      return true;
    }
  }

  return false;
};

/** Map pins: Hamburg street addresses only — not city/country centroids or other cities. */
const canShowOnMap = (pin) =>
  hasMapCoords(pin) &&
  hasUsableStreet(pin) &&
  isInHamburg(pin) &&
  !isVagueMapLocation(pin) &&
  !isCountryCentroid(pin);

const isOhneStrasseGroup = (pin) => !hasUsableStreet(pin) || !isInHamburg(pin);

const overlapGeoKey = (pin) =>
  `${Number(pin.lat).toFixed(5)},${Number(pin.lng).toFixed(5)}`;

const offsetOverlappingLatLng = (index, count, lat, lng) => {
  if (count <= 1) return { lat, lng };
  const angle =
    count <= 8
      ? (2 * Math.PI * index) / count - Math.PI / 2
      : index * 2.399963229728653;
  const radiusMeters =
    count <= 8 ? 6 + (count - 2) : 6 + 4 * Math.sqrt(index + 1);
  const dLat = (radiusMeters * Math.cos(angle)) / 111320;
  const dLng =
    (radiusMeters * Math.sin(angle)) / (111320 * Math.cos((lat * Math.PI) / 180));
  return { lat: lat + dLat, lng: lng + dLng };
};

/** Fan stacked pins out so every company at the same geocode is clickable. */
const spreadOverlappingPins = (pins) => {
  const groups = new Map();
  pins.forEach((pin) => {
    const key = overlapGeoKey(pin);
    const group = groups.get(key);
    if (group) group.push(pin);
    else groups.set(key, [pin]);
  });

  const placed = [];
  groups.forEach((group) => {
    const lat = Number(group[0].lat);
    const lng = Number(group[0].lng);
    group.forEach((pin, index) => {
      const pos = offsetOverlappingLatLng(index, group.length, lat, lng);
      placed.push({ pin, lat: pos.lat, lng: pos.lng });
    });
  });
  return placed;
};

const displayPosByPinId = (placed) => {
  const positions = new Map();
  placed.forEach(({ pin, lat, lng }) => {
    if (pin?.id) positions.set(pin.id, { lat, lng });
  });
  return positions;
};

/** Snap grouped route stops to the on-map (spread) pin instead of the shared geocode. */
const alignStopsToDisplayedPins = (stops, placed) => {
  const positions = displayPosByPinId(placed);
  return stops.map((stop) => {
    const firstId = stop.companies?.[0]?.id;
    const display = firstId ? positions.get(firstId) : null;
    return {
      ...stop,
      routeLat: stop.routeLat ?? stop.lat,
      routeLng: stop.routeLng ?? stop.lng,
      lat: display?.lat ?? stop.lat,
      lng: display?.lng ?? stop.lng,
    };
  });
};

/** Normalize address text so "Str." / punctuation variants still match. */
const normalizeAddressKey = (address) =>
  String(address || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\b(strasse|straße|str\.)\b/g, "str")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Collapse pins that share the same address into one stop.
 * Falls back to exact lat/lng only when no address is available.
 */
const groupPinsByLocation = (pins) => {
  const groups = new Map();

  pins.forEach((pin) => {
    if (!canShowOnMap(pin)) return;
    const lat = Number(pin.lat);
    const lng = Number(pin.lng);

    const addressLine = pinAddressLine(pin);
    const street = String(pin.address?.street || "").trim();
    const postalCode = String(pin.address?.postalCode || "").trim();
    const city = String(pin.address?.city || "").trim();
    const addressKey = normalizeAddressKey(addressLine);
    const key = addressKey
      ? `addr:${addressKey}`
      : `geo:${lat.toFixed(6)},${lng.toFixed(6)}`;

    const companyName = pinDisplayName(pin);
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        key,
        lat,
        lng,
        addressLine,
        street,
        postalCode,
        city,
        companies: [
          {
            id: pin.id,
            companyName,
            dealStageLabel: displayStageForPin(pin),
            phone: pin.phone || "",
            street,
            postalCode,
            city,
            addressLine,
          },
        ],
      });
      return;
    }

    if (!existing.addressLine && addressLine) existing.addressLine = addressLine;
    const already = existing.companies.some(
      (c) =>
        c.id === pin.id ||
        (companyName &&
          c.companyName.toLowerCase() === companyName.toLowerCase())
    );
    if (!already) {
      existing.companies.push({
        id: pin.id,
        companyName,
        dealStageLabel: displayStageForPin(pin),
        phone: pin.phone || "",
        street,
        postalCode,
        city,
        addressLine,
      });
    }
  });

  return [...groups.values()];
};

const HQ_ADDRESS_KEY = normalizeAddressKey(HQ.addressLine);

const stopMatchesOrigin = (stop, origin) => {
  const stopAddr = normalizeAddressKey(stop.addressLine);
  const originAddr = normalizeAddressKey(origin.addressLine || "");
  if (stopAddr && originAddr && stopAddr === originAddr) return true;

  // Always skip Cremon 11 when routing from HQ (even if address text differs slightly).
  if (originAddr === HQ_ADDRESS_KEY || origin.lat === HQ.lat) {
    if (stopAddr === HQ_ADDRESS_KEY) return true;
    if (
      Number.isFinite(stop.lat) &&
      Number.isFinite(stop.lng) &&
      distanceMeters(stop, { lat: HQ.lat, lng: HQ.lng }) < 40
    ) {
      return true;
    }
    const names = (stop.companies || []).map((c) =>
      String(c.companyName || "").toLowerCase()
    );
    if (names.some((n) => n.includes("dr. hoffmann") || n.includes("dr hoffmann"))) {
      return true;
    }
  }

  if (
    Number.isFinite(stop.lat) &&
    Number.isFinite(origin.lat) &&
    distanceMeters(stop, origin) < 25
  ) {
    return true;
  }
  return false;
};

/**
 * Closed tour length: origin → stops… → origin.
 */
const closedTourLength = (origin, stops) => {
  if (!stops.length) return 0;
  let len = distanceMeters(origin, stops[0]);
  for (let i = 0; i < stops.length - 1; i += 1) {
    len += distanceMeters(stops[i], stops[i + 1]);
  }
  len += distanceMeters(stops[stops.length - 1], origin);
  return len;
};

/**
 * 2-opt local search — untangles crossings and shortens the return-to-start leg.
 * Classic improvement for TSP-style loops (what nearest-neighbor is missing).
 */
const improveTourWith2Opt = (origin, stops) => {
  if (stops.length < 3) return stops;

  let route = [...stops];
  let improved = true;

  while (improved) {
    improved = false;
    for (let i = 0; i < route.length - 1; i += 1) {
      for (let k = i + 1; k < route.length; k += 1) {
        const a = i === 0 ? origin : route[i - 1];
        const b = route[i];
        const c = route[k];
        const d = k === route.length - 1 ? origin : route[k + 1];

        const before = distanceMeters(a, b) + distanceMeters(c, d);
        const after = distanceMeters(a, c) + distanceMeters(b, d);
        if (after + 0.5 < before) {
          route = [
            ...route.slice(0, i),
            ...route.slice(i, k + 1).reverse(),
            ...route.slice(k + 1),
          ];
          improved = true;
        }
      }
    }
  }

  return route;
};

/** Order stops around their centroid (circular / radial sweep). */
const orderByPolarAngle = (stops) => {
  if (stops.length < 2) return [...stops];
  const cx = stops.reduce((s, p) => s + p.lat, 0) / stops.length;
  const cy = stops.reduce((s, p) => s + p.lng, 0) / stops.length;
  return [...stops].sort(
    (a, b) =>
      Math.atan2(a.lng - cy, a.lat - cx) - Math.atan2(b.lng - cy, b.lat - cx)
  );
};

const buildNearestNeighborOrder = (origin, pool) => {
  const remaining = [...pool];
  const stops = [];
  let current = { lat: origin.lat, lng: origin.lng };

  while (remaining.length) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const d = distanceMeters(current, remaining[i]);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    stops.push(next);
    current = next;
  }

  return stops;
};

/**
 * Build a loop-friendly route (TSP-style):
 * 1) Take the closest N places to the start (work area)
 * 2) Order them as a circle (polar angle) and also via nearest-neighbor
 * 3) Improve both with 2-opt; keep the shorter closed tour
 *
 * That puts stops on the way out and back, instead of one long final jump home.
 */
const buildOptimizedRoute = (origin, candidates, maxStops = MAX_ROUTE_STOPS) => {
  const pool = candidates
    .filter((c) => !stopMatchesOrigin(c, origin))
    .sort(
      (a, b) => distanceMeters(origin, a) - distanceMeters(origin, b)
    )
    .slice(0, maxStops);

  if (!pool.length) return [];

  const candidatesOrders = [
    orderByPolarAngle(pool),
    buildNearestNeighborOrder(origin, pool),
  ].map((order) => improveTourWith2Opt(origin, order));

  let best = candidatesOrders[0];
  let bestLen = closedTourLength(origin, best);
  for (let i = 1; i < candidatesOrders.length; i += 1) {
    const len = closedTourLength(origin, candidatesOrders[i]);
    if (len < bestLen) {
      best = candidatesOrders[i];
      bestLen = len;
    }
  }

  return best;
};

const centroidOfStops = (stops) => ({
  lat: stops.reduce((sum, stop) => sum + Number(stop.lat), 0) / stops.length,
  lng: stops.reduce((sum, stop) => sum + Number(stop.lng), 0) / stops.length,
});

/** Two geographic clusters so each route covers a different area, no shared places. */
const splitIntoTwoClusters = (locations) => {
  if (locations.length < 2) return [locations, []];

  let seedA = locations[0];
  let seedB = locations[1];
  let farthest = -1;
  for (let i = 0; i < locations.length; i += 1) {
    for (let j = i + 1; j < locations.length; j += 1) {
      const dist = distanceMeters(locations[i], locations[j]);
      if (dist > farthest) {
        farthest = dist;
        seedA = locations[i];
        seedB = locations[j];
      }
    }
  }

  let centerA = { lat: seedA.lat, lng: seedA.lng };
  let centerB = { lat: seedB.lat, lng: seedB.lng };
  let groups = [[], []];

  for (let iter = 0; iter < 12; iter += 1) {
    const nextGroups = [[], []];
    const pointA = centerA;
    const pointB = centerB;
    for (let i = 0; i < locations.length; i += 1) {
      const stop = locations[i];
      const useFirst = distanceMeters(stop, pointA) <= distanceMeters(stop, pointB);
      nextGroups[useFirst ? 0 : 1].push(stop);
    }
    groups = nextGroups;
    if (!groups[0].length || !groups[1].length) break;
    centerA = centroidOfStops(groups[0]);
    centerB = centroidOfStops(groups[1]);
  }

  if (!groups[0].length || !groups[1].length) {
    const sorted = [...locations].sort((a, b) => a.lng - b.lng || a.lat - b.lat);
    const mid = Math.ceil(sorted.length / 2);
    return [sorted.slice(0, mid), sorted.slice(mid)];
  }

  const moveClosest = (from, to) => {
    const target = centroidOfStops(to);
    let bestIdx = 0;
    let bestDist = Infinity;
    from.forEach((stop, index) => {
      const dist = distanceMeters(stop, target);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = index;
      }
    });
    to.push(from.splice(bestIdx, 1)[0]);
  };

  while (groups[0].length > groups[1].length + 1) moveClosest(groups[0], groups[1]);
  while (groups[1].length > groups[0].length + 1) moveClosest(groups[1], groups[0]);

  return groups;
};

const buildTwoOptimizedRoutes = (origin, candidates, maxStops = MAX_ROUTE_STOPS) => {
  const pool = candidates
    .filter((stop) => !stopMatchesOrigin(stop, origin))
    .sort((a, b) => distanceMeters(origin, a) - distanceMeters(origin, b))
    .slice(0, maxStops * 2);

  const [groupA, groupB] = splitIntoTwoClusters(pool);
  return [
    buildOptimizedRoute(origin, groupA, maxStops),
    buildOptimizedRoute(origin, groupB, maxStops),
  ].filter((stops) => stops.length);
};

const ROUTE_LINE_PALETTES = [
  { from: [28, 42, 142], to: [220, 38, 38] },
  { from: [34, 120, 58], to: [201, 154, 39] },
];

const lerpChannel = (a, b, t) => Math.round(a + (b - a) * t);

const routeSegmentColor = (palette, index, total) => {
  const t = total <= 1 ? 0 : index / (total - 1);
  const r = lerpChannel(palette.from[0], palette.to[0], t);
  const g = lerpChannel(palette.from[1], palette.to[1], t);
  const b = lerpChannel(palette.from[2], palette.to[2], t);
  return `rgb(${r},${g},${b})`;
};

const stripPdfCityCountry = (address) =>
  String(address || "")
    .replace(/,?\s*hamburg\s*,?\s*(deutschland|germany)\s*$/i, "")
    .replace(/,?\s*(deutschland|germany)\s*$/i, "")
    .replace(/,?\s*hamburg\s*$/i, "")
    .replace(/[,\s]+$/g, "")
    .trim();

const routePhoneEntries = (companies = []) => {
  const seen = new Set();
  const entries = [];
  companies.forEach((company) => {
    const phone = String(company?.phone || "").trim();
    if (!phone) return;
    const key = phone.replace(/[\s/-]+/g, "");
    if (seen.has(key)) return;
    seen.add(key);
    entries.push({
      name: String(company?.companyName || "").trim(),
      phone,
    });
  });
  return entries;
};

const routePhoneLabel = (companies, t) => {
  const entries = routePhoneEntries(companies);
  if (!entries.length) return t("dealMap.noPhone");
  if (entries.length === 1) return `${t("dealMap.phone")}: ${entries[0].phone}`;
  return `${t("dealMap.phone")}: ${entries
    .map((entry) => (entry.name ? `${entry.name}: ${entry.phone}` : entry.phone))
    .join(" · ")}`;
};

const routePdfRows = (plan, t) => {
  const rows = [
    {
      order: 1,
      name: plan.origin?.label || t("dealMap.routeStart"),
      status: t("dealMap.routeStart"),
      address: stripPdfCityCountry(plan.origin?.addressLine || ""),
      phone: routePhoneEntries(plan.origin?.companies).map((entry) => entry.phone).join(", "),
    },
  ];
  (plan.stops || []).forEach((stop, index) => {
    const order = index + 2;
    const companies = stop.companies?.length ? stop.companies : [null];
    companies.forEach((company) => {
      rows.push({
        order,
        name: company?.companyName || stop.label || t("dealMap.unnamed"),
        status: company?.dealStageLabel || t("dealMap.unknownStage"),
        address: stripPdfCityCountry(
          company?.addressLine || stop.addressLine || t("dealMap.noAddress")
        ),
        phone: String(company?.phone || "").trim(),
      });
    });
  });
  return rows;
};

const exportRoutePdf = (plan, title, t) => {
  const rows = routePdfRows(plan, t);
  const today = new Date().toLocaleDateString("de-DE");
  const html = `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8"/>
<title>${escapeHtml(title)} – ${escapeHtml(today)}</title>
<style>
  body { font-family: Georgia, "Times New Roman", serif; color: #111; margin: 24px; }
  .pdf-head { display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; margin: 0 0 16px; }
  h1 { font-size: 18px; margin: 0; color: #1c2a8e; }
  .pdf-date { margin: 0; color: #444; font-size: 12px; white-space: nowrap; }
  .print-hint { font-size: 11px; color: #666; margin: 0 0 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { text-align: left; background: #1c2a8e; color: #fff; padding: 8px; font-weight: 600; }
  td { border-bottom: 1px solid #ddd; padding: 7px 8px; vertical-align: top; }
  td:first-child, th:first-child { width: 3.5rem; text-align: center; }
  @media print {
    body { margin: 12mm; }
    .print-hint { display: none; }
  }
</style>
</head>
<body>
  <div class="pdf-head">
    <h1>${escapeHtml(title)}</h1>
    <p class="pdf-date">${escapeHtml(today)}</p>
  </div>
  <p class="print-hint">${escapeHtml(t("dealMap.routePdfPrintHint"))}</p>
  <table>
    <thead>
      <tr>
        <th>${escapeHtml(t("dealMap.routePdfOrder"))}</th>
        <th>${escapeHtml(t("dealMap.routePdfName"))}</th>
        <th>${escapeHtml(t("dealMap.routePdfStatus"))}</th>
        <th>${escapeHtml(t("dealMap.routePdfAddress"))}</th>
        <th>${escapeHtml(t("dealMap.phone"))}</th>
      </tr>
    </thead>
    <tbody>
      ${rows
        .map(
          (row) => `<tr>
        <td>${row.order}</td>
        <td>${escapeHtml(row.name)}</td>
        <td>${escapeHtml(row.status)}</td>
        <td>${escapeHtml(row.address)}</td>
        <td>${escapeHtml(row.phone || (row.order === 1 ? "" : t("dealMap.noPhone")))}</td>
      </tr>`
        )
        .join("")}
    </tbody>
  </table>
</body>
</html>`;
  const popup = window.open("", "_blank");
  if (!popup) return false;
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  popup.focus();
  const printWhenReady = () => {
    popup.print();
  };
  if (popup.document.readyState === "complete") {
    setTimeout(printWhenReady, 50);
  } else {
    popup.onload = printWhenReady;
  }
  return true;
};

const APPLE_ROUTE_MODE = "cycling";
const GOOGLE_ROUTE_MODE = "bicycling";

const formatRouteCoord = (point) =>
  `${Number(point.lat).toFixed(6)},${Number(point.lng).toFixed(6)}`;

const routePlaceName = (point) => {
  const name = String(
    point?.companies?.[0]?.companyName ||
      point?.label ||
      point?.companyName ||
      ""
  ).trim();
  if (!name) return "";
  if (/^ohne firmenname$/i.test(name)) return "";
  return name;
};

const isGpsPoint = (point) =>
  Number.isFinite(Number(point?.lat)) &&
  Number.isFinite(Number(point?.lng)) &&
  !(point?.companies || []).length &&
  !String(point?.addressLine || "").trim();

const formatStreetLocation = ({ street, postalCode, city, addressLine } = {}) => {
  const streetPart = String(street || "").trim();
  const postal = String(postalCode || "").trim();
  const cityPart = String(city || "").trim();
  const line = String(addressLine || "").trim();
  if (streetPart) {
    const cityLine = [postal, cityPart || "Hamburg"].filter(Boolean).join(" ");
    return [streetPart, cityLine].filter(Boolean).join(", ");
  }
  return line;
};

const mapsAddressQuery = (point) => {
  if (isGpsPoint(point)) return formatRouteCoord(point);
  const company = point?.companies?.[0] || {};
  return (
    formatStreetLocation({
      street: point.street || company.street,
      postalCode: point.postalCode || company.postalCode,
      city: point.city || company.city,
      addressLine: point.addressLine || company.addressLine,
    }) || formatRouteCoord(point)
  );
};

const expandStopsForExternalMaps = (stops = []) => {
  const expanded = [];
  stops.forEach((stop) => {
    const companies =
      Array.isArray(stop.companies) && stop.companies.length ? stop.companies : [null];
    companies.forEach((company) => {
      expanded.push({
        lat: stop.lat,
        lng: stop.lng,
        addressLine: company?.addressLine || stop.addressLine || "",
        street: company?.street || stop.street || "",
        postalCode: company?.postalCode || stop.postalCode || "",
        city: company?.city || stop.city || "",
        label: company?.companyName || stop.label || "",
        companies: company ? [company] : [],
      });
    });
  });
  return expanded;
};

const buildGoogleMapsRouteUrl = (queries) => {
  const originQuery = queries[0];
  const stopQueries = queries.slice(1);
  const parts = [originQuery, ...stopQueries, originQuery].map((part) =>
    encodeURIComponent(part)
  );
  return `https://www.google.com/maps/dir/${parts.join("/")}?travelmode=${GOOGLE_ROUTE_MODE}`;
};

const buildAppleMapsRouteUrl = (queries) => {
  const originQuery = queries[0];
  const stopQueries = queries.slice(1);
  const params = new URLSearchParams();
  params.set("source", originQuery);
  params.set("mode", APPLE_ROUTE_MODE);
  if (stopQueries.length) {
    const last = stopQueries[stopQueries.length - 1];
    stopQueries.slice(0, -1).forEach((stop) => params.append("waypoint", stop));
    params.set("destination", last);
  } else {
    params.set("destination", originQuery);
  }
  return `https://maps.apple.com/directions?${params.toString()}`;
};

const buildRouteMapUrls = async (origin, stops, resolveQueries) => {
  const externalStops = expandStopsForExternalMaps(stops);
  const points = [origin, ...externalStops];
  const fallback = points.map((point) => mapsAddressQuery(point));
  let queries = fallback;
  try {
    const resolved = await resolveQueries(
      points.map((point) => ({
        name:
          isGpsPoint(point) || !(point?.companies || []).length
            ? ""
            : routePlaceName(point),
        addressQuery: mapsAddressQuery(point),
        lat: point.lat,
        lng: point.lng,
      }))
    );
    if (Array.isArray(resolved) && resolved.length === points.length) {
      queries = resolved.map((query, index) => query || fallback[index]);
    }
  } catch {
    queries = fallback;
  }
  return {
    appleMapsUrl: buildAppleMapsRouteUrl(queries),
    googleMapsUrl: buildGoogleMapsRouteUrl(queries),
  };
};

const originFromRouteStop = (stop, t) => ({
  lat: stop.lat,
  lng: stop.lng,
  routeLat: stop.routeLat ?? stop.lat,
  routeLng: stop.routeLng ?? stop.lng,
  label:
    stop.companies?.[0]?.companyName ||
    stop.label ||
    t("dealMap.unnamed"),
  addressLine: stop.addressLine || "",
  street: stop.street || stop.companies?.[0]?.street || "",
  postalCode: stop.postalCode || stop.companies?.[0]?.postalCode || "",
  city: stop.city || stop.companies?.[0]?.city || "",
  companies: stop.companies || [],
});

const buildPopupNotesListHtml = (pin, t) => {
  const notes = Array.isArray(pin?.adminNoteHistory) ? pin.adminNoteHistory : [];

  if (!notes.length) {
    return `<p class="team-deal-map-popup-notes-empty">${escapeHtml(
      t("dealMap.notesEmpty")
    )}</p>`;
  }

  return `<ul class="team-deal-map-popup-notes-list">${notes
    .map(
      (note) =>
        `<li><div class="team-deal-map-popup-note-meta"><span>${escapeHtml(
          formatSyncTime(note?.createdAt) || "—"
        )}</span><button type="button" data-deal-map-popup-note-delete data-note-id="${escapeHtml(
          note?.id || "legacy"
        )}" aria-label="${escapeHtml(t("dealMap.notesDelete"))}">${escapeHtml(
          t("dealMap.notesDelete")
        )}</button></div><p>${escapeHtml(note?.content || "")}</p></li>`
    )
    .join("")}</ul>`;
};

const buildPopupStageOptionsHtml = (pin, t) => {
  const currentStage = stageValueForPin(pin);
  return CRM_STAGE_OPTIONS.map((stage) => {
    const selected = stage === currentStage ? " selected" : "";
    return `<option value="${escapeHtml(stage)}"${selected}>${escapeHtml(
      t(`customerStatus.${stage}`)
    )}</option>`;
  }).join("");
};

const buildPopupHtml = (pin, t, isHq = false, canDeleteCompany = true) => {
  const name = escapeHtml(pinDisplayName(pin) || t("dealMap.unnamed"));
  const address = escapeHtml(
    pin.addressLine || pin.address?.fullAddress || t("dealMap.noAddress")
  );
  if (isHq) {
    return (
      `<strong>${name}</strong><br/>` +
      `${address}<br/>` +
      `<em>${escapeHtml(t("dealMap.hqLabel"))}</em>`
    );
  }
  const phone = escapeHtml(pin.phone || t("dealMap.noPhone"));
  const stage = escapeHtml(displayStageForPin(pin) || t("dealMap.unknownStage"));
  const lastContacted = escapeHtml(
    formatSyncTime(latestActivityMs(pin)) || t("dealMap.noLastContact")
  );
  const websiteValue = pinWebsiteValue(pin);
  const websiteUrl = normalizeWebsiteUrl(websiteValue);
  const websiteText = websiteUrl
    ? `<a href="${escapeHtml(websiteUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(
        websiteLabel(websiteValue)
      )}</a>`
    : escapeHtml(t("dealMap.noWebsite"));
  const popupCompanyDelete = canDeleteCompany
    ? `<button type="button" class="team-deal-map-popup-company-delete" data-deal-map-popup-delete-company>` +
      `${escapeHtml(t("dealMap.companyDelete"))}` +
      `</button>`
    : "";
  const popupNotes =
    `<section class="team-deal-map-popup-notes" data-deal-map-popup-notes>` +
    `<strong>${escapeHtml(t("dealMap.notesLabel"))}</strong>` +
    `<form data-deal-map-popup-note-form>` +
    `<textarea data-deal-map-popup-note-input maxlength="2000" placeholder="${escapeHtml(
      t("dealMap.notesPlaceholder")
    )}"></textarea>` +
    `<div class="team-deal-map-popup-notes-actions">` +
    `<span data-deal-map-popup-note-status role="status"></span>` +
    `<button type="submit" data-deal-map-popup-note-submit>${escapeHtml(
      t("dealMap.notesAdd")
    )}</button>` +
    `</div>` +
    `<div data-deal-map-popup-notes-list>${buildPopupNotesListHtml(pin, t)}</div>` +
    `</form>` +
    popupCompanyDelete +
    `</section>`;
  return (
    `<strong>${name}</strong><br/>` +
    `${address}<br/>` +
    `${escapeHtml(t("dealMap.phone"))}: ${phone}<br/>` +
    `${escapeHtml(t("dealMap.dealOwner"))}: ${escapeHtml(formatPinDealOwners(pin, t))}<br/>` +
    `${escapeHtml(t("dealMap.lastContactedAt"))}: ${lastContacted}<br/>` +
    `${escapeHtml(t("dealMap.website"))}: ${websiteText}<br/>` +
    `<em>${escapeHtml(t("dealMap.stage"))}: ${stage}</em>` +
    `<label class="team-deal-map-popup-stage">` +
    `<span>${escapeHtml(t("dealMap.stage"))}</span>` +
    `<select data-deal-map-popup-stage>${buildPopupStageOptionsHtml(pin, t)}</select>` +
    `<span data-deal-map-popup-stage-status role="status"></span>` +
    `</label>` +
    popupNotes
  );
};

const DealMapTab = ({
  apiBase = "/admin/hubspot/deal-pins",
  autoGenerateRouteRequest = 0,
}) => {
  const { t } = useTeamLocale();
  const canDeleteCompany = apiBase.startsWith("/admin/");
  const isEmployeeView = apiBase.startsWith("/employee/");
  const filterMenuId = useId();
  const mapShellRef = useRef(null);
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const hqLayerRef = useRef(null);
  const userLayerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const routingAttributionRef = useRef("");
  const routeRequestIdRef = useRef(0);
  const routeInteractionRef = useRef(0);
  const reservedCompanyIdsRef = useRef(new Set());
  const hasSavedRouteRef = useRef(false);
  const buildRoutePlanRef = useRef(null);
  const autoRouteHandledRef = useRef(0);
  const markersRef = useRef(new Map());
  const geoWatchIdRef = useRef(null);
  const geoRefreshIdRef = useRef(null);
  const geoRefreshPendingRef = useRef(false);
  const latestLocationTimestampRef = useRef(0);

  const [pins, setPins] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    withCoordinates: 0,
    missingCoordinates: 0,
    stages: [],
    lastSyncedAt: null,
  });
  const [stageFilter, setStageFilter] = useState("all");
  const [streetFilter, setStreetFilter] = useState("all");
  const [phoneFilter, setPhoneFilter] = useState("all");
  const [sortMode, setSortMode] = useState("name-asc");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stageSavingId, setStageSavingId] = useState(null);
  const [notesSavingId, setNotesSavingId] = useState(null);
  const [notesSavedId, setNotesSavedId] = useState(null);
  const [deletingPinId, setDeletingPinId] = useState(null);
  const [pinNoteDrafts, setPinNoteDrafts] = useState({});
  const [editingPinNote, setEditingPinNote] = useState(null);
  const [editingPinNoteText, setEditingPinNoteText] = useState("");
  const [deletingPinNoteId, setDeletingPinNoteId] = useState(null);
  const [notesDialogPinId, setNotesDialogPinId] = useState(null);
  const [detailsDialogPinId, setDetailsDialogPinId] = useState(null);
  const [companyDetailsForm, setCompanyDetailsForm] = useState(
    EMPTY_COMPANY_DETAILS_FORM
  );
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsMessage, setDetailsMessage] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [geoStatus, setGeoStatus] = useState("pending"); // pending | granted | denied | unavailable
  const [routeMessage, setRouteMessage] = useState(null);
  const [routePlans, setRoutePlans] = useState([]);
  const [activeRouteIndex, setActiveRouteIndex] = useState(0);
  const [routeLinesVisible, setRouteLinesVisible] = useState([true, true]);
  const [routeBusy, setRouteBusy] = useState(false);
  const [dayRouteChecked, setDayRouteChecked] = useState(false);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const nativeFullscreenRef = useRef(false);
  const isMapImmersive = isMapFullscreen;
  const filterMenuOpen = Boolean(filterAnchorEl);
  const activeFilterCount = [
    stageFilter !== "all",
    streetFilter !== "all",
    phoneFilter !== "all",
    sortMode !== "name-asc",
  ].filter(Boolean).length;

  const reopenPinPopup = useCallback((pinId) => {
    window.setTimeout(() => {
      const updatedMarker = markersRef.current.get(pinId);
      if (updatedMarker) updatedMarker.openPopup();
    }, 120);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(apiBase, {
        params: { brand: "dr-hoffmann", includeArchived: "true" },
      });
      const nextPins = data.pins || [];
      setPins(nextPins);
      setPinNoteDrafts(
        Object.fromEntries(nextPins.map((pin) => [pin.id, ""]))
      );
      setMeta(
        data.meta || {
          total: 0,
          withCoordinates: 0,
          missingCoordinates: 0,
          stages: [],
          lastSyncedAt: null,
        }
      );
    } catch (err) {
      setError(err?.response?.data?.message || t("dealMap.loadError"));
    } finally {
      setLoading(false);
    }
  }, [apiBase, t]);

  useEffect(() => {
    load();
  }, [load]);

  const updatePinStage = useCallback(
    async (pinId, stage) => {
      if (!pinId) return null;
      setStageSavingId(pinId);
      setError(null);
      try {
        const { data } = await api.patch(`${apiBase}/${pinId}/stage`, {
          stage,
        });
        if (data.pin) {
          setPins((prev) => prev.map((pin) => (pin.id === pinId ? data.pin : pin)));
          setMeta((prev) => ({
            ...prev,
            lastSyncedAt: data.pin.lastSyncedAt || prev.lastSyncedAt,
          }));
        }
        return data.pin || null;
      } catch (err) {
        setError(err?.response?.data?.message || t("dealMap.stageUpdateError"));
        return null;
      } finally {
        setStageSavingId(null);
      }
    },
    [apiBase, t]
  );

  const applyPinUpdate = (nextPin) => {
    if (!nextPin) return;
    setPins((prev) => prev.map((pin) => (pin.id === nextPin.id ? nextPin : pin)));
  };

  const savePinNote = useCallback(
    async (pinId, content) => {
      const noteContent = String(content || "");
      if (!pinId || !noteContent.trim()) return null;
      setNotesSavingId(pinId);
      setNotesSavedId(null);
      setError(null);
      try {
        const { data } = await api.post(
          `${apiBase}/${pinId}/notes`,
          { adminNotes: noteContent },
          { params: { brand: "dr-hoffmann" } }
        );
        if (data.pin) {
          setPins((prev) => prev.map((pin) => (pin.id === pinId ? data.pin : pin)));
          setNotesSavedId(pinId);
        }
        return data.pin || null;
      } catch (err) {
        setError(err?.response?.data?.message || t("dealMap.notesError"));
        return null;
      } finally {
        setNotesSavingId(null);
      }
    },
    [apiBase, t]
  );

  const updatePinNotes = async (pinId) => {
    const nextPin = await savePinNote(pinId, pinNoteDrafts[pinId] || "");
    if (nextPin) {
      setPinNoteDrafts((prev) => ({ ...prev, [pinId]: "" }));
    }
  };

  const deletePinNoteRequest = useCallback(
    async (pinId, noteId) => {
      if (!pinId || !noteId) return null;
      setDeletingPinNoteId(`${pinId}:${noteId}`);
      setNotesSavedId(null);
      setError(null);
      try {
        const { data } = await api.delete(
          `${apiBase}/${pinId}/notes/${noteId}`,
          { params: { brand: "dr-hoffmann" } }
        );
        if (data.pin) {
          setPins((prev) => prev.map((pin) => (pin.id === pinId ? data.pin : pin)));
          setNotesSavedId(pinId);
        }
        return data.pin || null;
      } catch (err) {
        setError(err?.response?.data?.message || t("dealMap.notesDeleteError"));
        return null;
      } finally {
        setDeletingPinNoteId(null);
      }
    },
    [apiBase, t]
  );

  const deleteCompany = useCallback(
    async (pinId) => {
      if (!pinId) return false;
      setDeletingPinId(pinId);
      setError(null);
      try {
        await api.delete(`${apiBase}/${pinId}`, {
          params: { brand: "dr-hoffmann" },
        });
        setPins((prev) => prev.filter((pin) => pin.id !== pinId));
        setPinNoteDrafts((prev) => {
          const next = { ...prev };
          delete next[pinId];
          return next;
        });
        setSelectedId((prev) => (prev === pinId ? null : prev));
        setNotesDialogPinId((prev) => (prev === pinId ? null : prev));
        setDetailsDialogPinId((prev) => (prev === pinId ? null : prev));
        setCompanyDetailsForm(EMPTY_COMPANY_DETAILS_FORM);
        return true;
      } catch (err) {
        setError(err?.response?.data?.message || t("dealMap.companyDeleteError"));
        return false;
      } finally {
        setDeletingPinId(null);
      }
    },
    [apiBase, t]
  );

  const bindPopupNotes = useCallback(
    (marker, pinId) => {
      marker.on("popupopen", ({ popup }) => {
        const popupElement = popup?.getElement?.();
        const form = popupElement?.querySelector("[data-deal-map-popup-note-form]");
        if (!form || form.dataset.bound === "true") return;
        form.dataset.bound = "true";

        const input = form.querySelector("[data-deal-map-popup-note-input]");
        const submit = form.querySelector("[data-deal-map-popup-note-submit]");
        const status = form.querySelector("[data-deal-map-popup-note-status]");
        const notesList = form.querySelector("[data-deal-map-popup-notes-list]");
        const stageSelect = popupElement?.querySelector("[data-deal-map-popup-stage]");
        const stageStatus = popupElement?.querySelector(
          "[data-deal-map-popup-stage-status]"
        );
        const deleteCompanyButton = popupElement?.querySelector(
          "[data-deal-map-popup-delete-company]"
        );

        if (stageSelect) {
          stageSelect.dataset.currentValue = stageSelect.value;
          stageSelect.addEventListener("change", async (event) => {
            const previousStage = stageSelect.dataset.currentValue || stageSelect.value;
            const nextStage = event.target.value;
            if (nextStage === previousStage) return;

            stageSelect.disabled = true;
            if (stageStatus) stageStatus.textContent = "";
            const nextPin = await updatePinStage(pinId, nextStage);
            if (nextPin) {
              stageSelect.dataset.currentValue = nextStage;
              if (stageStatus) stageStatus.textContent = t("dealMap.stageSaved");
              reopenPinPopup(pinId);
            } else {
              stageSelect.value = previousStage;
              if (stageStatus) stageStatus.textContent = t("dealMap.stageUpdateError");
            }
            stageSelect.disabled = false;
          });
        }

        form.addEventListener("submit", async (event) => {
          event.preventDefault();
          if (!input || !String(input.value || "").trim()) return;

          if (input) input.disabled = true;
          if (submit) submit.disabled = true;
          if (status) status.textContent = "";

          const nextPin = await savePinNote(pinId, input.value);
          if (nextPin) {
            input.value = "";
            if (notesList) {
              notesList.innerHTML = buildPopupNotesListHtml(nextPin, t);
            }
            if (status) status.textContent = t("dealMap.notesSaved");
            reopenPinPopup(pinId);
          } else if (status) {
            status.textContent = t("dealMap.notesError");
          }

          if (input) input.disabled = false;
          if (submit) submit.disabled = false;
        });

        notesList?.addEventListener("click", async (event) => {
          const deleteButton = event.target.closest?.(
            "[data-deal-map-popup-note-delete]"
          );
          if (!deleteButton || !notesList.contains(deleteButton)) return;
          const noteId = deleteButton.dataset.noteId;
          if (!noteId || !window.confirm(t("dealMap.notesDeleteConfirm"))) return;

          deleteButton.disabled = true;
          const nextPin = await deletePinNoteRequest(pinId, noteId);
          if (nextPin) {
            notesList.innerHTML = buildPopupNotesListHtml(nextPin, t);
            if (status) status.textContent = t("dealMap.notesDeleted");
            reopenPinPopup(pinId);
          } else {
            deleteButton.disabled = false;
          }
        });

        deleteCompanyButton?.addEventListener("click", async () => {
          if (!window.confirm(t("dealMap.companyDeleteConfirm"))) return;
          deleteCompanyButton.disabled = true;
          deleteCompanyButton.textContent = t("dealMap.companyDeleting");
          const deleted = await deleteCompany(pinId);
          if (deleted) popup.close();
          else {
            deleteCompanyButton.disabled = false;
            deleteCompanyButton.textContent = t("dealMap.companyDelete");
          }
        });
      });
    },
    [
      deleteCompany,
      deletePinNoteRequest,
      reopenPinPopup,
      savePinNote,
      t,
      updatePinStage,
    ]
  );

  const beginEditPinNote = (pinId, note) => {
    setNotesSavedId(null);
    setEditingPinNote({ pinId, noteId: note.id });
    setEditingPinNoteText(note.content || "");
  };

  const cancelEditPinNote = () => {
    setEditingPinNote(null);
    setEditingPinNoteText("");
  };

  const savePinNoteEdit = async (pinId, noteId) => {
    if (!pinId || !noteId || !editingPinNoteText.trim()) return;
    setNotesSavingId(pinId);
    setNotesSavedId(null);
    setError(null);
    try {
      const { data } = await api.patch(
        `${apiBase}/${pinId}/notes/${noteId}`,
        { adminNotes: editingPinNoteText },
        { params: { brand: "dr-hoffmann" } }
      );
      if (data.pin) {
        applyPinUpdate(data.pin);
        setNotesSavedId(pinId);
      }
      cancelEditPinNote();
    } catch (err) {
      setError(err?.response?.data?.message || t("dealMap.notesError"));
    } finally {
      setNotesSavingId(null);
    }
  };

  const deletePinNote = async (pinId, noteId) => {
    if (!pinId || !noteId) return;
    const nextPin = await deletePinNoteRequest(pinId, noteId);
    if (nextPin && editingPinNote?.pinId === pinId && editingPinNote?.noteId === noteId) {
      cancelEditPinNote();
    }
  };

  const openPinNotesDialog = (pinId) => {
    setNotesSavedId(null);
    setNotesDialogPinId(pinId);
  };

  const closePinNotesDialog = () => {
    setNotesDialogPinId(null);
    cancelEditPinNote();
  };

  const openPinDetailsDialog = (pin) => {
    setDetailsDialogPinId(pin.id);
    setCompanyDetailsForm(companyDetailsFormFromPin(pin));
    setDetailsMessage(null);
  };

  const closePinDetailsDialog = () => {
    if (detailsSaving) return;
    setDetailsDialogPinId(null);
    setCompanyDetailsForm(EMPTY_COMPANY_DETAILS_FORM);
    setDetailsMessage(null);
  };

  const updateCompanyDetailsField = (field, value) => {
    setCompanyDetailsForm((prev) => ({ ...prev, [field]: value }));
    setDetailsMessage(null);
  };

  const savePinDetails = async (event) => {
    event.preventDefault();
    if (!detailsDialogPinId) return;
    setDetailsSaving(true);
    setDetailsMessage(null);
    setError(null);
    try {
      const { data } = await api.patch(
        `${apiBase}/${detailsDialogPinId}`,
        {
          companyName: companyDetailsForm.companyName,
          contactName: companyDetailsForm.contactName,
          email: companyDetailsForm.email,
          phone: companyDetailsForm.phone,
          website: companyDetailsForm.website,
          domain: companyDetailsForm.domain,
          address: {
            fullAddress: companyDetailsForm.addressLine,
          },
        },
        { params: { brand: "dr-hoffmann" } }
      );
      if (data.pin) {
        applyPinUpdate(data.pin);
        setCompanyDetailsForm(companyDetailsFormFromPin(data.pin));
        setMeta((prev) => ({
          ...prev,
          lastSyncedAt: data.pin.lastSyncedAt || prev.lastSyncedAt,
        }));
      }
      setDetailsMessage({
        type: data.hubspotSynced ? "success" : "warning",
        text: data.hubspotSynced
          ? t("dealMap.detailsSaved")
          : data.hubspotMessage
            ? `${t("dealMap.detailsSavedLocalOnly")} ${data.hubspotMessage}`
            : t("dealMap.detailsSavedLocalOnly"),
      });
      await load();
    } catch (err) {
      setDetailsMessage({
        type: "error",
        text: err?.response?.data?.message || t("dealMap.detailsError"),
      });
    } finally {
      setDetailsSaving(false);
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const native = getFullscreenElement() === mapShellRef.current;
      if (native) {
        nativeFullscreenRef.current = true;
        setIsMapFullscreen(true);
      } else if (nativeFullscreenRef.current) {
        nativeFullscreenRef.current = false;
        setIsMapFullscreen(false);
      }
      setTimeout(() => invalidateMapIfReady(mapRef.current), 80);
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!notesDialogPinId && !detailsDialogPinId) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      setNotesDialogPinId(null);
      setEditingPinNote(null);
      setEditingPinNoteText("");
      if (!detailsSaving) {
        setDetailsDialogPinId(null);
        setDetailsMessage(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [notesDialogPinId, detailsDialogPinId, detailsSaving]);

  useEffect(() => {
    document.documentElement.classList.toggle(
      "team-deal-map-document-fullscreen",
      isMapImmersive
    );
    document.body.classList.toggle("team-deal-map-body-fullscreen", isMapImmersive);
    const timer = setTimeout(() => invalidateMapIfReady(mapRef.current), 80);

    return () => {
      clearTimeout(timer);
      document.documentElement.classList.remove("team-deal-map-document-fullscreen");
      document.body.classList.remove("team-deal-map-body-fullscreen");
    };
  }, [isMapImmersive]);

  useEffect(() => {
    if (!isMapImmersive) return undefined;

    const updateFullscreenHeight = () => {
      applyMapFullscreenViewport();
      setTimeout(() => invalidateMapIfReady(mapRef.current), 40);
    };

    updateFullscreenHeight();
    window.addEventListener("resize", updateFullscreenHeight);
    window.addEventListener("orientationchange", updateFullscreenHeight);
    window.visualViewport?.addEventListener("resize", updateFullscreenHeight);
    window.visualViewport?.addEventListener("scroll", updateFullscreenHeight);

    return () => {
      window.removeEventListener("resize", updateFullscreenHeight);
      window.removeEventListener("orientationchange", updateFullscreenHeight);
      window.visualViewport?.removeEventListener("resize", updateFullscreenHeight);
      window.visualViewport?.removeEventListener("scroll", updateFullscreenHeight);
      clearMapFullscreenViewport();
    };
  }, [isMapImmersive]);

  const requestUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoStatus("unavailable");
      return;
    }

    const updateLocation = (pos) => {
      if (pos.timestamp <= latestLocationTimestampRef.current) return;
      latestLocationTimestampRef.current = pos.timestamp;
      setUserLocation({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      });
      setGeoStatus("granted");
    };

    setGeoStatus("pending");
    navigator.geolocation.getCurrentPosition(
      updateLocation,
      (err) => {
        setGeoStatus(err?.code === 1 ? "denied" : "unavailable");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );

    if (geoWatchIdRef.current != null) {
      navigator.geolocation.clearWatch(geoWatchIdRef.current);
    }
    geoWatchIdRef.current = navigator.geolocation.watchPosition(
      updateLocation,
      () => {
        /* keep last known; getCurrentPosition already set status */
      },
      { enableHighAccuracy: true, maximumAge: 0 }
    );

    if (geoRefreshIdRef.current != null) {
      window.clearInterval(geoRefreshIdRef.current);
    }
    geoRefreshIdRef.current = window.setInterval(() => {
      if (geoRefreshPendingRef.current) return;
      geoRefreshPendingRef.current = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          geoRefreshPendingRef.current = false;
          updateLocation(pos);
        },
        () => {
          geoRefreshPendingRef.current = false;
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    }, 3000);
  }, []);

  useEffect(() => {
    requestUserLocation();
    return () => {
      if (geoWatchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(geoWatchIdRef.current);
        geoWatchIdRef.current = null;
      }
      if (geoRefreshIdRef.current != null) {
        window.clearInterval(geoRefreshIdRef.current);
        geoRefreshIdRef.current = null;
      }
    };
  }, [requestUserLocation]);

  const legendStages = FILTER_STAGE_LABELS;
  const lastSyncedLabel = formatSyncTime(meta.lastSyncedAt);

  const filteredPins = useMemo(() => {
    return pins.filter((pin) => {
      if (stageFilter !== "all" && stageKeyForPin(pin) !== stageFilter) return false;
      const hasPhone = Boolean(String(pin.phone || "").trim());
      return phoneFilter === "all" || (phoneFilter === "has" ? hasPhone : !hasPhone);
    });
  }, [pins, stageFilter, phoneFilter]);

  const sortedPins = useMemo(
    () => sortPins(filteredPins, sortMode),
    [filteredPins, sortMode]
  );

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const searchedPins = useMemo(() => {
    if (!normalizedQuery) return sortedPins;
    return sortedPins.filter((p) => {
      const haystack = [
        p.companyName,
        p.contactName,
        p.contactFullName,
        p.fullName,
        p.email,
        p.dealName,
        p.phone,
        p.website,
        p.domain,
        formatSyncTime(latestActivityMs(p)),
        displayStageForPin(p),
        formatPinDealOwners(p, t),
        ...(Array.isArray(p.deals)
          ? p.deals.flatMap((deal) => [
              deal?.ownerName,
              deal?.owner,
              deal?.ownerEmail,
              deal?.dealName,
            ])
          : []),
        p.dealOwnerName,
        p.dealOwnerEmail,
        p.addressLine,
        p.address?.fullAddress,
        p.address?.street,
        p.address?.city,
        p.address?.postalCode,
        p.adminNotes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [sortedPins, normalizedQuery, t]);

  const hqMatchesSearch = useMemo(() => {
    if (!normalizedQuery) return true;
    const haystack = [HQ.companyName, HQ.addressLine, "unser standort", "dr. hoffmann"]
      .join(" ")
      .toLowerCase();
    return haystack.includes(normalizedQuery);
  }, [normalizedQuery]);

  const listedPins = useMemo(() => {
    if (streetFilter === "missing") return searchedPins.filter(isOhneStrasseGroup);
    if (streetFilter === "has") {
      return searchedPins.filter((p) => hasUsableStreet(p) && isInHamburg(p));
    }
    return searchedPins;
  }, [searchedPins, streetFilter]);

  // Map pins always require a street — the Straße list filter never plots city/ZIP/country-only companies.
  const mappablePins = useMemo(
    () =>
      searchedPins
        .filter(
          (pin) =>
            stageKeyForPin(pin) !== RESPONSE_WAIT_STAGE_LABEL ||
            stageFilter === RESPONSE_WAIT_STAGE_LABEL
        )
        .filter(canShowOnMap),
    [searchedPins, stageFilter]
  );

  const placedPins = useMemo(
    () => spreadOverlappingPins(mappablePins),
    [mappablePins]
  );

  const stopNumberByPinId = useMemo(() => {
    const numbers = new Map();
    routePlans.forEach((plan, routeIndex) => {
      if (routeLinesVisible[routeIndex] === false) return;
      (plan.origin?.companies || []).forEach((company) => {
        if (company?.id) numbers.set(company.id, { number: 1, routeIndex });
      });
      (plan.stops || []).forEach((stop, index) => {
        const number = index + 2;
        (stop.companies || []).forEach((company) => {
          if (company?.id) numbers.set(company.id, { number, routeIndex });
        });
      });
    });
    return numbers;
  }, [routePlans, routeLinesVisible]);

  const activeRoutePlan = routePlans[activeRouteIndex] || routePlans[0] || null;
  const originMode = activeRoutePlan?.originMode;
  const showOriginStopNumber =
    Boolean(originMode) &&
    routePlans.some(
      (plan, index) =>
        plan.originMode === originMode && routeLinesVisible[index] !== false
    );

  const missingPins = useMemo(
    () => listedPins.filter((p) => !canShowOnMap(p)),
    [listedPins]
  );

  const notesDialogPin = useMemo(
    () => pins.find((pin) => pin.id === notesDialogPinId) || null,
    [notesDialogPinId, pins]
  );

  const detailsDialogPin = useMemo(
    () => pins.find((pin) => pin.id === detailsDialogPinId) || null,
    [detailsDialogPinId, pins]
  );

  // Always mount the map (even while loading / with zero deal pins).
  useEffect(() => {
    if (!mapNodeRef.current || mapRef.current) return undefined;

    const map = L.map(mapNodeRef.current, {
      center: [HQ.lat, HQ.lng],
      zoom: 16,
      scrollWheelZoom: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    hqLayerRef.current = L.layerGroup().addTo(map);
    userLayerRef.current = L.layerGroup().addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    routeLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    const kick = () => invalidateMapIfReady(map);
    const t1 = setTimeout(kick, 50);
    const t2 = setTimeout(kick, 250);
    window.addEventListener("resize", kick);

    return () => {
      window.removeEventListener("resize", kick);
      clearTimeout(t1);
      clearTimeout(t2);
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
      hqLayerRef.current = null;
      userLayerRef.current = null;
      routeLayerRef.current = null;
      markersRef.current = new Map();
    };
  }, []);

  // Fixed HQ pin — always present, visually distinct from deal pins.
  useEffect(() => {
    const map = mapRef.current;
    const hqLayer = hqLayerRef.current;
    if (!map || !hqLayer) return;

    hqLayer.clearLayers();
    const marker = L.marker([HQ.lat, HQ.lng], {
      icon: makeHqIcon(originMode === "hq" && showOriginStopNumber ? 1 : null),
      zIndexOffset: 1000,
    });
    marker.bindPopup(buildPopupHtml(HQ, t, true));
    marker.on("click", () => setSelectedId(HQ.id));
    marker.addTo(hqLayer);
    markersRef.current.set(HQ.id, marker);
  }, [t, originMode, showOriginStopNumber]);

  // User location pin
  useEffect(() => {
    const map = mapRef.current;
    const userLayer = userLayerRef.current;
    if (!map || !userLayer) return;

    userLayer.clearLayers();
    markersRef.current.delete("user-location");

    if (
      !userLocation ||
      !Number.isFinite(userLocation.lat) ||
      !Number.isFinite(userLocation.lng)
    ) {
      return;
    }

    const marker = L.marker([userLocation.lat, userLocation.lng], {
      icon: makeUserIcon(originMode === "user" && showOriginStopNumber ? 1 : null),
      zIndexOffset: 1200,
    });
    marker.bindPopup(`<strong>${escapeHtml(t("dealMap.userLocation"))}</strong>`);
    marker.on("click", () => setSelectedId("user-location"));
    marker.addTo(userLayer);
    markersRef.current.set("user-location", marker);
  }, [userLocation, t, originMode, showOriginStopNumber]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();
    for (const key of [...markersRef.current.keys()]) {
      if (key !== HQ.id && key !== "user-location") markersRef.current.delete(key);
    }

    placedPins.forEach(({ pin, lat, lng }) => {
      const color = colorForPin(pin);
      const stopInfo = stopNumberByPinId.get(pin.id) || null;
      const marker = L.marker([lat, lng], {
        icon: makeStageIcon(color, stopInfo?.number ?? null, stopInfo?.routeIndex ?? 0),
        zIndexOffset: stopInfo ? 700 + stopInfo.number : 0,
      });
      marker.bindPopup(buildPopupHtml(pin, t, false, canDeleteCompany));
      bindPopupNotes(marker, pin.id);
      marker.on("click", () => setSelectedId(pin.id));
      marker.addTo(layer);
      markersRef.current.set(pin.id, marker);
    });

    // Keep the default view on Cremon 11 — do not fitBounds to all pins.
    const timer = setTimeout(() => invalidateMapIfReady(map), 50);
    return () => clearTimeout(timer);
  }, [placedPins, stopNumberByPinId, t, bindPopupNotes, canDeleteCompany]);

  // Draw route segments: dark blue → light blue across the loop
  useEffect(() => {
    const routeLayer = routeLayerRef.current;
    if (!routeLayer) return;

    routeLayer.clearLayers();
    if (!routePlans.length) return;

    routePlans.forEach((plan, routeIndex) => {
      if (!plan?.origin || !plan.stops?.length) return;
      if (routeLinesVisible[routeIndex] === false) return;
      const palette = ROUTE_LINE_PALETTES[routeIndex] || ROUTE_LINE_PALETTES[0];
      const { geometry, wayPoints } = plan.cycleRoute || {};
      if (!Array.isArray(geometry) || !Array.isArray(wayPoints)) return;
      const totalSegments = wayPoints.length - 1;
      for (let i = 0; i < totalSegments; i += 1) {
        const segment = geometry.slice(wayPoints[i], wayPoints[i + 1] + 1);
        if (segment.length < 2) continue;
        L.polyline(segment.map(([lng, lat]) => [lat, lng]), {
          color: routeSegmentColor(palette, i, totalSegments),
          weight: 4,
          opacity: 0.95,
          lineCap: "round",
          lineJoin: "round",
        }).addTo(routeLayer);
      }
    });
  }, [routePlans, routeLinesVisible]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !routePlans.length) return;
    const points = routePlans.flatMap((plan) => plan.cycleRoute?.geometry || [])
      .map(([lng, lat]) => [lat, lng]);
    if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points), { padding: [30, 30], maxZoom: 16 });
    }
  }, [routePlans]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.attributionControl) return;
    const provider = routePlans.find((plan) => plan?.cycleRoute?.provider)?.cycleRoute.provider;
    const next = provider === "google"
      ? 'Routing &copy; <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer">Google</a>'
      : provider === "openrouteservice"
        ? 'Routing &copy; <a href="https://openrouteservice.org/" target="_blank" rel="noopener noreferrer">openrouteservice.org</a> by HeiGIT'
        : "";
    const previous = routingAttributionRef.current;
    if (previous && previous !== next) map.attributionControl.removeAttribution(previous);
    if (next && previous !== next) map.attributionControl.addAttribution(next);
    routingAttributionRef.current = next;
  }, [routePlans]);

  const focusPin = (pin) => {
    setSelectedId(pin.id);
    const map = mapRef.current;
    if (!map || !canShowOnMap(pin)) {
      return;
    }
    const marker = markersRef.current.get(pin.id);
    const latLng = marker?.getLatLng();
    map.setView([latLng?.lat ?? pin.lat, latLng?.lng ?? pin.lng], 16);
    if (marker) marker.openPopup();
  };

  const focusRouteCompanyId = (companyId) => {
    if (!companyId) return false;
    const pin = pins.find((p) => p.id === companyId);
    if (!pin) return false;
    focusPin(pin);
    return true;
  };

  const focusRouteStop = (stop) => {
    if (focusRouteCompanyId(stop?.companies?.[0]?.id)) return;
    const map = mapRef.current;
    if (map && Number.isFinite(stop?.lat) && Number.isFinite(stop?.lng)) {
      map.setView([stop.lat, stop.lng], 16);
    }
  };

  const focusRouteOrigin = (plan = activeRoutePlan) => {
    if (!plan?.origin) return;
    if (plan.originMode === "hq") {
      focusPin(HQ);
      return;
    }
    if (focusRouteCompanyId(plan.origin?.companies?.[0]?.id)) return;
    const map = mapRef.current;
    const marker = markersRef.current.get("user-location");
    setSelectedId("user-location");
    if (map) map.setView([plan.origin.lat, plan.origin.lng], 16);
    if (marker) marker.openPopup();
  };

  const toggleRouteLine = (routeIndex) => {
    setRouteLinesVisible((prev) => {
      const next = [...prev];
      next[routeIndex] = prev[routeIndex] === false;
      return next;
    });
  };

  const resolveMapsQueries = async (points) => {
    const { data } = await api.post(`${apiBase}/maps-queries`, { points });
    return data?.queries;
  };

  const buildRoutePlan = async (origin, stops, originMode) => {
    const coordinates = [origin, ...stops, origin].map((point) => [
      Number(point.routeLng ?? point.lng),
      Number(point.routeLat ?? point.lat),
    ]);
    const [urls, { data: cycleRoute }] = await Promise.all([
      buildRouteMapUrls(origin, stops, resolveMapsQueries),
      api.post(apiBase + "/cycle-route", { coordinates }),
    ]);
    return { originMode, origin, stops, ...urls, cycleRoute };
  };
  buildRoutePlanRef.current = buildRoutePlan;

  const planForStorage = (plan) => ({
    originMode: plan.originMode,
    origin: plan.origin,
    stops: plan.stops,
    appleMapsUrl: plan.appleMapsUrl,
    googleMapsUrl: plan.googleMapsUrl,
  });

  const rememberReservedCompanies = (ids = []) => {
    const next = new Set(ids.filter(Boolean));
    reservedCompanyIdsRef.current = next;
  };

  const saveRoutePlans = async (plans) => {
    const stored = plans.map(planForStorage);
    if (!stored.length) {
      await api.delete(`${apiBase}/day-route`, { params: { brand: "dr-hoffmann" } });
      hasSavedRouteRef.current = false;
      return;
    }
    const { data } = await api.put(
      `${apiBase}/day-route`,
      { plans: stored },
      { params: { brand: "dr-hoffmann" } }
    );
    hasSavedRouteRef.current = true;
    if (Array.isArray(data?.reservedCompanyIds)) {
      rememberReservedCompanies(data.reservedCompanyIds);
    }
  };

  const generateRoutes = async (nextOriginMode = "user", routeCount = 1) => {
    const requestId = ++routeRequestIdRef.current;
    routeInteractionRef.current += 1;
    hasSavedRouteRef.current = true;
    setRouteMessage(t("dealMap.routeResolving"));
    setRouteBusy(true);

    const origin =
      nextOriginMode === "hq"
        ? {
            lat: HQ.lat,
            lng: HQ.lng,
            label: t("dealMap.hqName"),
            addressLine: HQ.addressLine,
            street: HQ.address.street,
            postalCode: HQ.address.postalCode,
            city: HQ.address.city,
          }
        : userLocation && Number.isFinite(userLocation.lat) && Number.isFinite(userLocation.lng)
          ? {
              lat: userLocation.lat,
              lng: userLocation.lng,
              label: t("dealMap.userLocation"),
              addressLine: "",
            }
          : null;

    if (!origin) {
      setRouteBusy(false);
      setRouteMessage(t("dealMap.routeNeedLocation"));
      requestUserLocation();
      return;
    }

    const reserved = new Set(reservedCompanyIdsRef.current);
    let savedPlans = null;

    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const eligiblePins = mappablePins.filter(isRouteEligiblePin);
        const routePins = eligiblePins.filter((pin) => !reserved.has(pin.id));
        if (!routePins.length) {
          setRouteMessage(
            eligiblePins.length ? t("dealMap.routeAllHeld") : t("dealMap.routeNoPins")
          );
          return;
        }

        const uniqueLocations = groupPinsByLocation(routePins);
        const stopSets =
          routeCount > 1
            ? buildTwoOptimizedRoutes(origin, uniqueLocations, MAX_ROUTE_STOPS).map((stops) =>
                alignStopsToDisplayedPins(stops, placedPins)
              )
            : [
                alignStopsToDisplayedPins(
                  buildOptimizedRoute(origin, uniqueLocations, MAX_ROUTE_STOPS),
                  placedPins
                ),
              ];
        const usableStopSets = stopSets.filter((stops) => stops.length);

        if (routeCount > 1 && usableStopSets.length < 2) {
          setRouteMessage(t("dealMap.routeNeedTwoPlaces"));
          return;
        }
        if (!usableStopSets.length) {
          setRouteMessage(t("dealMap.routeNoPins"));
          return;
        }

        const plans = await Promise.all(
          usableStopSets.map((stops) => buildRoutePlan(origin, stops, nextOriginMode))
        );
        if (requestId !== routeRequestIdRef.current) return;

        try {
          await saveRoutePlans(plans);
          savedPlans = plans;
          break;
        } catch (err) {
          const taken = err?.response?.data?.companyIds;
          if (err?.response?.status === 409 && Array.isArray(taken) && taken.length && attempt < 2) {
            taken.forEach((id) => reserved.add(id));
            rememberReservedCompanies([...reservedCompanyIdsRef.current, ...taken]);
            continue;
          }
          throw err;
        }
      }

      if (requestId !== routeRequestIdRef.current || !savedPlans) return;
      setRoutePlans(savedPlans);
      setActiveRouteIndex(0);
      setRouteLinesVisible([true, true]);
      if (savedPlans.length > 1) {
        setRouteMessage(
          t("dealMap.routeReadyTwo", {
            a: savedPlans[0].stops.length,
            b: savedPlans[1].stops.length,
          })
        );
      } else {
        setRouteMessage(
          t(nextOriginMode === "hq" ? "dealMap.routeReadyFromHq" : "dealMap.routeReady", {
            count: savedPlans[0].stops.length,
          })
        );
      }
    } catch (err) {
      if (requestId === routeRequestIdRef.current) {
        setRouteMessage(err?.response?.data?.message || t("dealMap.routeSaveFailed"));
      }
    } finally {
      if (requestId === routeRequestIdRef.current) setRouteBusy(false);
    }
  };

  const generateRoutesRef = useRef(generateRoutes);
  generateRoutesRef.current = generateRoutes;

  useEffect(() => {
    let cancelled = false;
    const seenInteraction = routeInteractionRef.current;
    (async () => {
      try {
        const { data } = await api.get(`${apiBase}/day-route`, {
          params: { brand: "dr-hoffmann" },
        });
        if (cancelled || routeInteractionRef.current !== seenInteraction) return;
        const reserved = Array.isArray(data?.reservedCompanyIds) ? data.reservedCompanyIds : [];
        rememberReservedCompanies(reserved);
        const stored = Array.isArray(data?.plans) ? data.plans : [];
        if (!stored.length) return;
        setRouteBusy(true);
        setRouteMessage(t("dealMap.routeResolving"));
        const plans = [];
        for (const plan of stored) {
          if (!plan?.origin || !plan?.stops?.length) continue;
          try {
            plans.push(
              await buildRoutePlanRef.current(plan.origin, plan.stops, plan.originMode)
            );
          } catch {
            plans.push(plan);
          }
          if (cancelled || routeInteractionRef.current !== seenInteraction) return;
        }
        if (!plans.length || cancelled || routeInteractionRef.current !== seenInteraction) return;
        hasSavedRouteRef.current = true;
        setRoutePlans(plans);
        setActiveRouteIndex(0);
        setRouteLinesVisible([true, true]);
        setRouteMessage(t("dealMap.routeRestored"));
      } catch {
        if (!cancelled && routeInteractionRef.current === seenInteraction) {
          setRouteMessage(null);
        }
      } finally {
        if (!cancelled && routeInteractionRef.current === seenInteraction) {
          setRouteBusy(false);
        }
        if (!cancelled) setDayRouteChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, t]);

  useEffect(() => {
    if (
      !dayRouteChecked ||
      hasSavedRouteRef.current ||
      !autoGenerateRouteRequest ||
      autoRouteHandledRef.current >= autoGenerateRouteRequest ||
      loading ||
      !userLocation
    ) return;
    autoRouteHandledRef.current = autoGenerateRouteRequest;
    generateRoutesRef.current("user", 1);
  }, [autoGenerateRouteRequest, dayRouteChecked, loading, userLocation]);

  const applyRouteStops = async (
    origin, stops, nextOriginMode = activeRoutePlan?.originMode,
    routeIndex = activeRouteIndex
  ) => {
    const requestId = ++routeRequestIdRef.current;
    routeInteractionRef.current += 1;
    if (!stops.length) {
      const nextPlans = routePlans.filter((_, index) => index !== routeIndex);
      setRouteBusy(true);
      try {
        await saveRoutePlans(nextPlans);
      } catch (err) {
        if (requestId === routeRequestIdRef.current) {
          setRouteMessage(err?.response?.data?.message || t("dealMap.routeSaveFailed"));
        }
        return;
      } finally {
        if (requestId === routeRequestIdRef.current) setRouteBusy(false);
      }
      if (requestId !== routeRequestIdRef.current) return;
      setRoutePlans(nextPlans);
      setActiveRouteIndex((current) => {
        if (routeIndex < current) return current - 1;
        if (current >= routeIndex) return Math.max(0, current - 1);
        return current;
      });
      setRouteMessage(nextPlans.length ? t("dealMap.routeKeptToday") : null);
      return;
    }
    setRouteBusy(true);
    setRouteMessage(t("dealMap.routeResolving"));
    try {
      const plan = await buildRoutePlan(origin, stops, nextOriginMode);
      if (requestId !== routeRequestIdRef.current) return;
      const nextPlans = routePlans.map((current, index) => index === routeIndex ? plan : current);
      await saveRoutePlans(nextPlans);
      if (requestId !== routeRequestIdRef.current) return;
      setRoutePlans(nextPlans);
      setRouteMessage(t("dealMap.routeKeptToday"));
    } catch (err) {
      if (requestId === routeRequestIdRef.current) {
        setRouteMessage(err?.response?.data?.message || t("dealMap.routeSaveFailed"));
      }
    } finally {
      if (requestId === routeRequestIdRef.current) setRouteBusy(false);
    }
  };

  const removeRouteStop = (stopKey, routeIndex = activeRouteIndex) => {
    const plan = routePlans[routeIndex];
    if (!plan) return;
    applyRouteStops(
      plan.origin,
      plan.stops.filter((s) => s.key !== stopKey),
      plan.originMode,
      routeIndex
    );
  };

  const removeRouteOrigin = (routeIndex = activeRouteIndex) => {
    const plan = routePlans[routeIndex];
    if (!plan?.stops?.length) {
      applyRouteStops(plan?.origin, [], plan?.originMode, routeIndex);
      return;
    }
    const [nextOrigin, ...stops] = plan.stops;
    applyRouteStops(originFromRouteStop(nextOrigin, t), stops, "stop", routeIndex);
  };

  const clearRoute = async () => {
    const requestId = ++routeRequestIdRef.current;
    routeInteractionRef.current += 1;
    setRouteBusy(true);
    try {
      await saveRoutePlans([]);
    } catch (err) {
      if (requestId === routeRequestIdRef.current) {
        setRouteMessage(err?.response?.data?.message || t("dealMap.routeSaveFailed"));
      }
      return;
    } finally {
      if (requestId === routeRequestIdRef.current) setRouteBusy(false);
    }
    if (requestId !== routeRequestIdRef.current) return;
    setRoutePlans([]);
    setActiveRouteIndex(0);
    setRouteLinesVisible([true, true]);
    setRouteMessage(null);
  };

  const handleExportRoutePdf = (plan, routeIndex) => {
    const title =
      routePlans.length > 1
        ? t("dealMap.routeListTitleN", { n: routeIndex + 1 })
        : t("dealMap.routeListTitle");
    if (!exportRoutePdf(plan, title, t)) {
      setRouteMessage(t("dealMap.routePdfBlocked"));
    }
  };

  const zoomToUserLocation = () => {
    setRouteMessage(null);
    if (!userLocation || !Number.isFinite(userLocation.lat) || !Number.isFinite(userLocation.lng)) {
      setRouteMessage(t("dealMap.routeNeedLocation"));
      requestUserLocation();
      return;
    }
    const map = mapRef.current;
    if (!map) return;
    map.setView([userLocation.lat, userLocation.lng], 16);
    setSelectedId("user-location");
    const marker = markersRef.current.get("user-location");
    if (marker) marker.openPopup();
  };

  const toggleMapFullscreen = async () => {
    const shell = mapShellRef.current;
    if (!shell) return;

    if (isMapFullscreen) {
      if (getFullscreenElement()) {
        await exitFullscreen();
      }
      nativeFullscreenRef.current = false;
      setIsMapFullscreen(false);
      return;
    }

    applyMapFullscreenViewport();
    setIsMapFullscreen(true);
    try {
      await requestFullscreen(shell);
      nativeFullscreenRef.current = getFullscreenElement() === shell;
    } catch {
      nativeFullscreenRef.current = false;
    }
    setTimeout(() => invalidateMapIfReady(mapRef.current), 80);
  };

  return (
    <div className="team-deal-map">
      <div className="team-deal-map-toolbar">
        <div>
          <h2>{t(isEmployeeView ? "tabs.dealmap" : "dealMap.title")}</h2>
          {!isEmployeeView && <p className="team-muted">{t("dealMap.lead")}</p>}
          {lastSyncedLabel && (
            <p className="team-muted team-deal-map-sync">
              {t("dealMap.lastSynced", { time: lastSyncedLabel })}
            </p>
          )}
        </div>
        <div className="team-deal-map-actions">
          <button
            type="button"
            className="btn-ghost team-deal-map-filter-toggle"
            aria-haspopup="dialog"
            aria-expanded={filterMenuOpen}
            aria-controls={filterMenuOpen ? filterMenuId : undefined}
            onClick={(event) => setFilterAnchorEl(event.currentTarget)}
          >
            <TuneIcon fontSize="small" aria-hidden="true" />
            <span>{t("dealMap.filters")}</span>
            {activeFilterCount > 0 && (
              <span
                className="team-deal-map-filter-count"
                aria-label={t("dealMap.filtersActive", { count: activeFilterCount })}
              >
                {activeFilterCount}
              </span>
            )}
            <ExpandMoreIcon fontSize="small" aria-hidden="true" />
          </button>
          <Popover
            id={filterMenuId}
            open={filterMenuOpen}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            marginThreshold={16}
            disableScrollLock={false}
            className="team-deal-map-filter-popover"
            PaperProps={{
              className: "team-deal-map-filter-menu",
              role: "dialog",
              "aria-labelledby": `${filterMenuId}-title`,
            }}
          >
            <div className="team-deal-map-filter-menu-header">
              <h3 id={`${filterMenuId}-title`}>{t("dealMap.filtersTitle")}</h3>
              <button
                type="button"
                className="team-deal-map-filter-reset"
                disabled={activeFilterCount === 0}
                onClick={() => {
                  setStageFilter("all");
                  setStreetFilter("all");
                  setPhoneFilter("all");
                  setSortMode("name-asc");
                }}
              >
                {t("common.reset")}
              </button>
            </div>
            <div className="team-deal-map-filter-grid">
              <label className="team-deal-map-filter">
                <span>{t("dealMap.stageFilter")}</span>
                <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                  <option value="all">{t("common.all")}</option>
                  {legendStages.map((stage) => (
                    <option key={stage} value={stage}>
                      {stage === UNKNOWN_STAGE_LABEL ? t("dealMap.unknownStage") : stage}
                    </option>
                  ))}
                </select>
              </label>
              <label className="team-deal-map-filter">
                <span>{t("dealMap.streetFilter")}</span>
                <select value={streetFilter} onChange={(e) => setStreetFilter(e.target.value)}>
                  <option value="all">{t("common.all")}</option>
                  <option value="missing">{t("dealMap.streetFilterMissing")}</option>
                  <option value="has">{t("dealMap.streetFilterHas")}</option>
                </select>
              </label>
              <label className="team-deal-map-filter">
                <span>{t("dealMap.phoneFilter")}</span>
                <select value={phoneFilter} onChange={(e) => setPhoneFilter(e.target.value)}>
                  <option value="all">{t("common.all")}</option>
                  <option value="has">{t("dealMap.phoneFilterHas")}</option>
                  <option value="missing">{t("dealMap.phoneFilterMissing")}</option>
                </select>
              </label>
              <label className="team-deal-map-filter">
                <span>{t("dealMap.sortLabel")}</span>
                <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
                  <option value="name-asc">{t("dealMap.sortNameAsc")}</option>
                  <option value="last-contacted-desc">{t("dealMap.sortLastContactDesc")}</option>
                  <option value="last-contacted-asc">{t("dealMap.sortLastContactAsc")}</option>
                </select>
              </label>
            </div>
            <div className="team-deal-map-filter-menu-footer">
              <button type="button" className="btn-primary" onClick={() => setFilterAnchorEl(null)}>
                {t("common.close")}
              </button>
            </div>
          </Popover>
          <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
            {loading ? t("common.loading") : t("dealMap.refresh")}
          </button>
        </div>
      </div>

      <div className="team-deal-map-stats">
        <span>{t("dealMap.statTotal", { count: listedPins.length })}</span>
        <span>{t("dealMap.statMapped", { count: mappablePins.length })}</span>
        <span>{t("dealMap.statMissing", { count: missingPins.length })}</span>
      </div>

      <div className="team-deal-map-rotate-hint" role="note">
        <span className="team-deal-map-rotate-phone" aria-hidden="true">
          <ScreenRotationIcon fontSize="small" />
        </span>
        <span>{t("dealMap.rotateHint")}</span>
      </div>

      {error && <div className="auth-alert is-error">{error}</div>}

      <div className="team-deal-map-layout">
        <div
          ref={mapShellRef}
          className={`team-deal-map-canvas-wrap${isMapImmersive ? " is-fullscreen" : ""}`}
        >
          {loading && (
            <div className="team-deal-map-loading" role="status">
              <span className="team-deal-map-loading-spinner" aria-hidden="true" />
              <span>{t("dealMap.loadingCompanies")}</span>
            </div>
          )}
          <div className="team-deal-map-overlay-controls">
            <button
              type="button"
              className="team-deal-map-location-btn"
              onClick={zoomToUserLocation}
            >
              <MyLocationIcon fontSize="small" />
              <span>{t("dealMap.zoomToMe")}</span>
            </button>
            <button
              type="button"
              className="team-deal-map-fullscreen-btn"
              onClick={toggleMapFullscreen}
              aria-label={
                isMapFullscreen
                  ? t("dealMap.fullscreenExit")
                  : t("dealMap.fullscreenEnter")
              }
              title={
                isMapFullscreen
                  ? t("dealMap.fullscreenExit")
                  : t("dealMap.fullscreenEnter")
              }
            >
              {isMapFullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </button>
          </div>
          <div className="team-deal-map-rotate-hint team-deal-map-rotate-hint--map" role="note">
            <span className="team-deal-map-rotate-phone" aria-hidden="true">
              <ScreenRotationIcon fontSize="small" />
            </span>
            <span>{t("dealMap.rotateHint")}</span>
          </div>
          <div ref={mapNodeRef} className="team-deal-map-canvas" role="presentation" />
        </div>

        <div className="team-deal-map-route-bar">
          <button
            type="button"
            className="btn-primary team-deal-map-route-btn"
            disabled={routeBusy}
            onClick={() => generateRoutes("user", 1)}
          >
            {t("dealMap.routeGenerate")}
          </button>
          {!isEmployeeView && (
            <button
              type="button"
              className="btn-ghost team-deal-map-route-btn"
              disabled={routeBusy}
              onClick={() => generateRoutes("user", 2)}
            >
              {t("dealMap.routeGenerateTwoFromUser")}
            </button>
          )}
          <button
            type="button"
            className="btn-ghost team-deal-map-route-btn"
            disabled={routeBusy}
            onClick={() => generateRoutes("hq", 1)}
          >
            {t("dealMap.routeGenerateFromHq")}
          </button>
          {!isEmployeeView && (
            <button
              type="button"
              className="btn-ghost team-deal-map-route-btn"
              disabled={routeBusy}
              onClick={() => generateRoutes("hq", 2)}
            >
              {t("dealMap.routeGenerateTwoFromHq")}
            </button>
          )}
          {geoStatus === "pending" && !userLocation && (
            <span className="team-muted">{t("dealMap.locationPending")}</span>
          )}
          {geoStatus === "granted" && userLocation && (
            <span className="team-muted">{t("dealMap.locationReady")}</span>
          )}
          {(geoStatus === "denied" || geoStatus === "unavailable") && (
            <span className="team-deal-map-warn">{t("dealMap.locationDenied")}</span>
          )}
        </div>
        {routeMessage && <p className="team-muted team-deal-map-route-msg">{routeMessage}</p>}
        {routePlans.length > 0 && (
          <p className="team-muted team-deal-map-route-msg">{t("dealMap.routeKeptToday")}</p>
        )}

        {routePlans.length > 0 && (
          <div
            className={`team-deal-map-route-lists${
              routePlans.length > 1 ? " team-deal-map-route-lists--two" : ""
            }`}
          >
            {routePlans.map((plan, routeIndex) => {
              const title =
                routePlans.length > 1
                  ? t("dealMap.routeListTitleN", { n: routeIndex + 1 })
                  : t("dealMap.routeListTitle");
              const isActive = routeIndex === activeRouteIndex;
              return (
                <section
                  key={`route-${routeIndex}`}
                  className={`team-deal-map-route-list${
                    isActive ? " is-active" : ""
                  } team-deal-map-route-list--${routeIndex + 1}`}
                  aria-label={title}
                  onClick={() => setActiveRouteIndex(routeIndex)}
                >
                  <div className="team-deal-map-route-list-header">
                    <h3>{title}</h3>
                    <div className="team-deal-map-route-list-actions">
                      <button
                        type="button"
                        className="btn-ghost"
                        aria-pressed={routeLinesVisible[routeIndex] !== false}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRouteLine(routeIndex);
                        }}
                      >
                        {routeLinesVisible[routeIndex] === false
                          ? t("dealMap.routeShowLine")
                          : t("dealMap.routeHideLine")}
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportRoutePdf(plan, routeIndex);
                        }}
                      >
                        {t("dealMap.routeExportPdf")}
                      </button>
                      <a
                        className="btn-ghost"
                        href={plan.appleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t("dealMap.routeOpenApple")}
                      </a>
                      <a
                        className="btn-primary"
                        href={plan.googleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t("dealMap.routeOpenGoogle")}
                      </a>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (routePlans.length > 1) {
                            applyRouteStops(plan.origin, [], plan.originMode, routeIndex);
                          } else {
                            clearRoute();
                          }
                        }}
                      >
                        {t("dealMap.routeClear")}
                      </button>
                    </div>
                  </div>
                  <ol>
                    <li
                      className="team-deal-map-route-stop is-start"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        if (e.target.closest(".team-deal-map-route-remove")) return;
                        setActiveRouteIndex(routeIndex);
                        focusRouteOrigin(plan);
                      }}
                    >
                      <span className="team-deal-map-route-stop-num">1</span>
                      <span className="team-deal-map-route-stop-copy">
                        <strong>{plan.origin.label}</strong>
                        {plan.origin.addressLine ? (
                          <span>{plan.origin.addressLine}</span>
                        ) : (
                          <span>{t("dealMap.routeStart")}</span>
                        )}
                        {routePhoneEntries(plan.origin.companies).length > 0 && (
                          <span>{routePhoneLabel(plan.origin.companies, t)}</span>
                        )}
                      </span>
                      <button
                        type="button"
                        className="team-deal-map-route-remove"
                        onClick={() => removeRouteOrigin(routeIndex)}
                        aria-label={t("dealMap.routeRemoveStop")}
                        title={t("dealMap.routeRemoveStop")}
                      >
                        ×
                      </button>
                    </li>
                    {plan.stops.map((stop, index) => (
                      <li
                        key={stop.key || `${stop.lat},${stop.lng},${index}`}
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          if (e.target.closest(".team-deal-map-route-remove")) return;
                          setActiveRouteIndex(routeIndex);
                          focusRouteStop(stop);
                        }}
                      >
                        <span className="team-deal-map-route-stop-num">{index + 2}</span>
                        <span className="team-deal-map-route-stop-copy">
                          <strong className="team-deal-map-route-companies">
                            {stop.companies.map((c, companyIdx) => (
                              <span key={c.id || `${stop.key}-${companyIdx}`}>
                                {c.companyName || t("dealMap.unnamed")}
                              </span>
                            ))}
                          </strong>
                          <span>{stop.addressLine || t("dealMap.noAddress")}</span>
                          <span>{routePhoneLabel(stop.companies, t)}</span>
                          {stop.companies.length > 1 && (
                            <em>
                              {t("dealMap.routeCompaniesAtStop", {
                                count: stop.companies.length,
                              })}
                            </em>
                          )}
                        </span>
                        <button
                          type="button"
                          className="team-deal-map-route-remove"
                          onClick={() => removeRouteStop(stop.key, routeIndex)}
                          aria-label={t("dealMap.routeRemoveStop")}
                          title={t("dealMap.routeRemoveStop")}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                    <li
                      className="team-deal-map-route-stop is-end"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setActiveRouteIndex(routeIndex);
                        focusRouteOrigin(plan);
                      }}
                    >
                      <span className="team-deal-map-route-stop-num">
                        {plan.stops.length + 2}
                      </span>
                      <span className="team-deal-map-route-stop-copy">
                        <strong>{plan.origin.label}</strong>
                        <span>{t("dealMap.routeReturn")}</span>
                      </span>
                    </li>
                  </ol>
                </section>
              );
            })}
          </div>
        )}

        <div className="team-deal-map-legend" aria-label={t("dealMap.legendAria")}>
          <span>
            <i className="team-deal-map-legend-user" aria-hidden="true" />
            {t("dealMap.userLocation")}
          </span>
          <span>
            <i className="team-deal-map-legend-hq" aria-hidden="true" />
            {t("dealMap.hqLabel")}
          </span>
          {routePlans.length > 0 && (
            <>
              {routePlans.map((_, routeIndex) => {
                const visible = routeLinesVisible[routeIndex] !== false;
                return (
                  <button
                    key={`legend-route-${routeIndex}`}
                    type="button"
                    className={`team-deal-map-legend-toggle${
                      visible ? "" : " is-off"
                    }`}
                    aria-pressed={visible}
                    onClick={() => toggleRouteLine(routeIndex)}
                  >
                    <i
                      className={`team-deal-map-legend-route-${routeIndex + 1}`}
                      aria-hidden="true"
                    />
                    {routePlans.length > 1
                      ? t("dealMap.routeListTitleN", { n: routeIndex + 1 })
                      : t("dealMap.routeListTitle")}
                  </button>
                );
              })}
            </>
          )}
          {legendStages.map((stage) => (
            <span
              key={stage}
              title={EXPLAINED_STAGES.includes(stage)
                ? t(`dealMap.stageDescription.${stage.toLowerCase()}`)
                : undefined}
            >
              <i style={{ background: colorForStage(stage) }} aria-hidden="true" />
              {stage === LOST_STAGE_LABEL
                ? t("dealMap.lostStageLegend")
                : stage === UNKNOWN_STAGE_LABEL
                  ? t("dealMap.unknownStage")
                  : stage}
            </span>
          ))}
        </div>

        <DealMapStageHelp stages={EXPLAINED_STAGES} colors={STAGE_COLORS} />

        <aside className="team-deal-map-list" aria-busy={loading}>
          <div className="team-deal-map-list-header">
            <h3>{t("dealMap.listTitle")}</h3>
            <label className="team-deal-map-search">
              <span className="sr-only">{t("dealMap.searchLabel")}</span>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("dealMap.searchPlaceholder")}
                autoComplete="off"
              />
            </label>
          </div>
          <ul>
            {hqMatchesSearch && streetFilter !== "missing" && phoneFilter === "all" && (
              <li className="team-deal-map-hq-entry">
                <button
                  type="button"
                  className={selectedId === HQ.id ? "is-active is-hq" : "is-hq"}
                  onClick={() => focusPin(HQ)}
                >
                  <span className="team-deal-map-dot team-deal-map-dot--hq" aria-hidden="true" />
                  <span className="team-deal-map-list-copy">
                    <strong>{t("dealMap.hqName")}</strong>
                    <span>{HQ.addressLine}</span>
                    <em>{t("dealMap.hqLabel")}</em>
                  </span>
                </button>
              </li>
            )}
            {loading && pins.length === 0 && [0, 1, 2].map((index) => (
              <li key={`loading-${index}`} aria-hidden="true">
                <div className="team-deal-map-loading-card">
                  <span />
                  <span />
                  <span />
                </div>
              </li>
            ))}
            {listedPins.map((pin) => {
              const showOnMap = canShowOnMap(pin);
              return (
                <li key={pin.id}>
                  <div className={`team-deal-map-list-card ${selectedId === pin.id ? "is-active" : ""}`}>
                    <button
                      type="button"
                      className="team-deal-map-list-main"
                      onClick={() => focusPin(pin)}
                    >
                      <span
                        className="team-deal-map-dot"
                        style={{
                          background: colorForPin(pin),
                        }}
                        aria-hidden="true"
                      />
                      <span className="team-deal-map-list-copy">
                        <strong>{pinDisplayName(pin) || t("dealMap.unnamed")}</strong>
                        <span>
                          {pin.addressLine || pin.address?.fullAddress || t("dealMap.noAddress")}
                        </span>
                        <span>
                          {pin.phone
                            ? `${t("dealMap.phone")}: ${pin.phone}`
                            : t("dealMap.noPhone")}
                        </span>
                        <span>
                          {`${t("dealMap.dealOwner")}: ${formatPinDealOwners(pin, t)}`}
                        </span>
                        <span>
                          {`${t("dealMap.lastContactedAt")}: ${
                            formatSyncTime(latestActivityMs(pin)) || t("dealMap.noLastContact")
                          }`}
                        </span>
                        <span>
                          {pinWebsiteValue(pin)
                            ? `${t("dealMap.website")}: ${websiteLabel(pinWebsiteValue(pin))}`
                            : `${t("dealMap.website")}: ${t("dealMap.noWebsite")}`}
                        </span>
                        <em>{displayStageForPin(pin) || t("dealMap.unknownStage")}</em>
                        {!showOnMap && (
                          <span className="team-deal-map-warn">
                            {t(
                              !hasUsableStreet(pin)
                                ? "dealMap.noStreet"
                                : !isInHamburg(pin)
                                  ? "dealMap.outsideHamburg"
                                  : isVagueMapLocation(pin) || isCountryCentroid(pin)
                                    ? "dealMap.vagueLocation"
                                    : "dealMap.notGeocoded"
                            )}
                          </span>
                        )}
                      </span>
                    </button>
                    <label className="team-deal-map-stage-editor">
                      <span>{t("dealMap.stage")}</span>
                      <select
                        className="team-status-select"
                        value={stageValueForPin(pin)}
                        disabled={stageSavingId === pin.id}
                        onChange={(event) => updatePinStage(pin.id, event.target.value)}
                      >
                        {CRM_STAGE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {t(`customerStatus.${option}`)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="team-deal-map-card-actions">
                      <button
                        type="button"
                        className="btn-ghost team-deal-map-details-open"
                        onClick={() => openPinDetailsDialog(pin)}
                      >
                        {t("dealMap.detailsEdit")}
                      </button>
                      {canDeleteCompany && (
                        <button
                          type="button"
                          className="btn-ghost team-deal-map-company-delete"
                          disabled={deletingPinId === pin.id}
                          onClick={() => {
                            if (window.confirm(t("dealMap.companyDeleteConfirm"))) {
                              deleteCompany(pin.id);
                            }
                          }}
                        >
                          {deletingPinId === pin.id
                            ? t("dealMap.companyDeleting")
                            : t("dealMap.companyDelete")}
                        </button>
                      )}
                    </div>
                    <form
                      className="team-deal-map-notes"
                      onSubmit={(event) => {
                        event.preventDefault();
                        updatePinNotes(pin.id);
                      }}
                    >
                      <label htmlFor={`team-deal-map-notes-${pin.id}`}>
                        {t("dealMap.notesLabel")}
                      </label>
                      <textarea
                        id={`team-deal-map-notes-${pin.id}`}
                        value={pinNoteDrafts[pin.id] ?? ""}
                        onChange={(event) => {
                          const value = event.target.value;
                          setPinNoteDrafts((prev) => ({ ...prev, [pin.id]: value }));
                          if (notesSavedId === pin.id) setNotesSavedId(null);
                        }}
                        placeholder={t("dealMap.notesPlaceholder")}
                        maxLength={2000}
                        disabled={notesSavingId === pin.id}
                      />
                      <div className="team-deal-map-notes-actions">
                        {notesSavedId === pin.id && (
                          <span>{t("dealMap.notesSaved")}</span>
                        )}
                        <button
                          type="submit"
                          className="btn-primary"
                          disabled={notesSavingId === pin.id || !String(pinNoteDrafts[pin.id] || "").trim()}
                        >
                          {notesSavingId === pin.id ? t("common.saving") : t("dealMap.notesAdd")}
                        </button>
                      </div>
                      <div className="team-deal-map-note-preview">
                        {(pin.adminNoteHistory || []).length ? (
                          <>
                            <p>
                              <span>
                                {t("dealMap.notesPreviewMeta", {
                                  count: pin.adminNoteHistory.length,
                                  date:
                                    formatSyncTime(pin.adminNoteHistory[0]?.createdAt) ||
                                    "—",
                                })}
                              </span>
                              {pin.adminNoteHistory[0]?.content}
                            </p>
                            <button
                              type="button"
                              className="btn-ghost team-deal-map-notes-open"
                              onClick={() => openPinNotesDialog(pin.id)}
                            >
                              {t("dealMap.notesShowAll")}
                            </button>
                          </>
                        ) : (
                          <p className="team-muted">{t("dealMap.notesEmpty")}</p>
                        )}
                      </div>
                    </form>
                  </div>
                </li>
              );
            })}
            {!loading && !listedPins.length && (
              <li>
                <p className="team-muted team-deal-map-list-empty">
                  {normalizedQuery
                    ? t("dealMap.searchEmpty")
                    : phoneFilter !== "all" || stageFilter !== "all" || streetFilter !== "all"
                      ? t("dealMap.filterEmpty")
                      : t("dealMap.listEmpty")}
                </p>
              </li>
            )}
          </ul>
        </aside>
      </div>
      {notesDialogPin && (
        <div className="team-deal-map-notes-dialog-root">
          <button
            type="button"
            className="team-deal-map-notes-dialog-overlay"
            onClick={closePinNotesDialog}
            aria-label={t("common.close")}
          />
          <section
            className="team-deal-map-notes-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-deal-map-notes-dialog-title"
          >
            <div className="team-detail-header">
              <div>
                <p className="team-kicker">{t("dealMap.notesLabel")}</p>
                <h3 id="team-deal-map-notes-dialog-title">
                  {notesDialogPin.companyName || notesDialogPin.dealName || t("dealMap.unnamed")}
                </h3>
              </div>
              <button
                type="button"
                className="team-close"
                onClick={closePinNotesDialog}
                aria-label={t("common.close")}
              >
                ×
              </button>
            </div>
            <div className="team-note-history">
              {(notesDialogPin.adminNoteHistory || []).length ? (
                notesDialogPin.adminNoteHistory.map((note) => {
                  const isEditing =
                    editingPinNote?.pinId === notesDialogPin.id &&
                    editingPinNote?.noteId === note.id;
                  const deletingKey = `${notesDialogPin.id}:${note.id}`;
                  return (
                    <article className="team-note-history-item" key={note.id}>
                      <div className="team-note-history-meta">
                        <span>
                          {t("dealMap.notesCreatedAt", {
                            date: note.createdAt ? formatSyncTime(note.createdAt) : "—",
                          })}
                        </span>
                        {note.updatedAt && note.updatedAt !== note.createdAt ? (
                          <span>
                            {t("dealMap.notesUpdatedAt", {
                              date: formatSyncTime(note.updatedAt),
                            })}
                          </span>
                        ) : null}
                      </div>
                      {isEditing ? (
                        <>
                          <textarea
                            value={editingPinNoteText}
                            onChange={(event) =>
                              setEditingPinNoteText(event.target.value)
                            }
                            maxLength={2000}
                            disabled={notesSavingId === notesDialogPin.id}
                          />
                          <div className="team-note-history-actions">
                            <button
                              type="button"
                              className="btn-ghost"
                              onClick={cancelEditPinNote}
                              disabled={notesSavingId === notesDialogPin.id}
                            >
                              {t("common.cancel")}
                            </button>
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => savePinNoteEdit(notesDialogPin.id, note.id)}
                              disabled={
                                notesSavingId === notesDialogPin.id ||
                                !editingPinNoteText.trim()
                              }
                            >
                              {notesSavingId === notesDialogPin.id
                                ? t("common.saving")
                                : t("common.save")}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p>{note.content}</p>
                          <div className="team-note-history-actions">
                            <button
                              type="button"
                              className="btn-ghost"
                              onClick={() => beginEditPinNote(notesDialogPin.id, note)}
                              disabled={
                                notesSavingId === notesDialogPin.id ||
                                deletingPinNoteId === deletingKey
                              }
                            >
                              {t("dealMap.notesEdit")}
                            </button>
                            <button
                              type="button"
                              className="btn-ghost danger"
                              onClick={() => deletePinNote(notesDialogPin.id, note.id)}
                              disabled={
                                notesSavingId === notesDialogPin.id ||
                                deletingPinNoteId === deletingKey
                              }
                            >
                              {deletingPinNoteId === deletingKey
                                ? t("dealMap.notesDeleting")
                                : t("dealMap.notesDelete")}
                            </button>
                          </div>
                        </>
                      )}
                    </article>
                  );
                })
              ) : (
                <p className="team-muted">{t("dealMap.notesEmpty")}</p>
              )}
            </div>
          </section>
        </div>
      )}
      {detailsDialogPin && (
        <div className="team-deal-map-notes-dialog-root">
          <button
            type="button"
            className="team-deal-map-notes-dialog-overlay"
            onClick={closePinDetailsDialog}
            aria-label={t("common.close")}
          />
          <section
            className="team-deal-map-notes-dialog team-deal-map-details-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-deal-map-details-dialog-title"
          >
            <div className="team-detail-header">
              <div>
                <p className="team-kicker">{t("dealMap.detailsKicker")}</p>
                <h3 id="team-deal-map-details-dialog-title">
                  {detailsDialogPin.companyName ||
                    detailsDialogPin.dealName ||
                    t("dealMap.unnamed")}
                </h3>
              </div>
              <button
                type="button"
                className="team-close"
                onClick={closePinDetailsDialog}
                aria-label={t("common.close")}
                disabled={detailsSaving}
              >
                ×
              </button>
            </div>
            <form className="team-deal-map-details-form" onSubmit={savePinDetails}>
              <label>
                <span>{t("dealMap.companyName")}</span>
                <input
                  type="text"
                  value={companyDetailsForm.companyName}
                  onChange={(event) =>
                    updateCompanyDetailsField("companyName", event.target.value)
                  }
                  maxLength={200}
                  disabled={detailsSaving}
                />
              </label>
              <label>
                <span>{t("dealMap.contactName")}</span>
                <input
                  type="text"
                  value={companyDetailsForm.contactName}
                  onChange={(event) =>
                    updateCompanyDetailsField("contactName", event.target.value)
                  }
                  maxLength={200}
                  disabled={detailsSaving}
                />
              </label>
              <label>
                <span>{t("dealMap.email")}</span>
                <input
                  type="email"
                  value={companyDetailsForm.email}
                  onChange={(event) =>
                    updateCompanyDetailsField("email", event.target.value)
                  }
                  maxLength={160}
                  disabled={detailsSaving}
                />
              </label>
              <label>
                <span>{t("dealMap.phone")}</span>
                <input
                  type="tel"
                  value={companyDetailsForm.phone}
                  onChange={(event) =>
                    updateCompanyDetailsField("phone", event.target.value)
                  }
                  maxLength={60}
                  disabled={detailsSaving}
                />
              </label>
              <label>
                <span>{t("dealMap.website")}</span>
                <input
                  type="text"
                  value={companyDetailsForm.website}
                  onChange={(event) =>
                    updateCompanyDetailsField("website", event.target.value)
                  }
                  maxLength={300}
                  disabled={detailsSaving}
                />
              </label>
              <label>
                <span>{t("dealMap.domain")}</span>
                <input
                  type="text"
                  value={companyDetailsForm.domain}
                  onChange={(event) =>
                    updateCompanyDetailsField("domain", event.target.value)
                  }
                  maxLength={200}
                  disabled={detailsSaving}
                />
              </label>
              <label className="team-deal-map-details-form-wide">
                <span>{t("dealMap.address")}</span>
                <input
                  type="text"
                  value={companyDetailsForm.addressLine}
                  onChange={(event) =>
                    updateCompanyDetailsField("addressLine", event.target.value)
                  }
                  maxLength={400}
                  disabled={detailsSaving}
                />
              </label>
              {detailsMessage && (
                <p
                  className={`team-deal-map-details-message is-${detailsMessage.type}`}
                >
                  {detailsMessage.text}
                </p>
              )}
              <div className="team-deal-map-details-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={closePinDetailsDialog}
                  disabled={detailsSaving}
                >
                  {t("common.cancel")}
                </button>
                <button type="submit" className="btn-primary" disabled={detailsSaving}>
                  {detailsSaving ? t("common.saving") : t("common.save")}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
};

export default DealMapTab;
